<template>
  <div class="sports-app">
    <div class="scroll-progress" aria-hidden="true">
      <span :style="{ width: scrollProgress + '%' }"></span>
    </div>

    <div class="utility-bar">
      <span>2026年6月9日 · 中文体育门户</span>
      <router-link to="/live">直播中心</router-link>
      <router-link to="/videos">视频</router-link>
      <router-link to="/teams">球队库</router-link>
      <router-link to="/calendar">赛程日历</router-link>
      <router-link to="/newsletter">订阅</router-link>
      <router-link to="/search">全站搜索</router-link>
    </div>

<header class="topbar">
      <router-link class="brand" to="/" aria-label="全场脉冲首页">
        <span class="brand-mark">SP</span>
        <span>
          <strong>全场脉冲 Pro</strong>
          <small>中文综合体育门户</small>
        </span>
      </router-link>
      <nav class="quick-nav" aria-label="站内导航">
        <router-link to="/" exact>首页</router-link>
        <router-link to="/scores">比分</router-link>
        <router-link to="/features">专题</router-link>
        <router-link to="/guides">指南</router-link>
        <router-link to="/news">新闻</router-link>
        <router-link to="/events">赛事</router-link>
        <router-link to="/calendar">赛程</router-link>
        <router-link to="/leagues">联赛</router-link>
        <router-link to="/live">直播</router-link>
        <router-link to="/videos">视频</router-link>
        <router-link to="/teams">球队</router-link>
        <router-link to="/rankings">排名</router-link>
        <router-link to="/players">球星</router-link>
        <router-link to="/data">数据</router-link>
        <router-link to="/watch">清单</router-link>
        <router-link to="/sources">来源</router-link>
      </nav>
    </header>

    <nav class="channel-nav" aria-label="项目频道导航">
      <router-link
        v-for="sport in sports.filter((item) => item.id !== 'all')"
        :key="sport.id"
        :to="`/sports/${sport.id}`"
        :style="{ '--accent': sport.color }"
      >
        {{ sport.name }}
      </router-link>
      <span></span>
      <router-link to="/tags/NBA总决赛">NBA总决赛</router-link>
      <router-link to="/tags/世界杯">世界杯</router-link>
      <router-link to="/tags/WTT">WTT</router-link>
      <router-link to="/leagues">联赛中心</router-link>
      <router-link to="/calendar">赛程日历</router-link>
      <router-link to="/cities">城市</router-link>
      <router-link to="/newsletter">订阅</router-link>
      <router-link to="/search">搜索</router-link>
    </nav>

    <div class="headline-strip" aria-label="站内快讯">
      <strong>快讯</strong>
      <router-link v-for="item in headlineLinks" :key="item.to" :to="item.to">
        {{ item.label }}
      </router-link>
    </div>

    <main id="top">
      <section v-if="isHomeRoute" class="command-center" :style="heroStyle">
        <div class="hero-copy">
          <span class="eyebrow">更新至 {{ todayLabel }}</span>
          <h1>{{ activeSportTitle }}综合体育情报中心</h1>
          <p>{{ activeSport.intro }}</p>

          <div class="control-bar">
            <label class="search-box" for="searchInput">
              <i data-lucide="search"></i>
              <input
                id="searchInput"
                v-model.trim="query"
                type="search"
                placeholder="搜索新闻、球队、运动员、赛事、城市"
                @keyup.enter="goSearch(query)"
              />
            </label>
            <button class="icon-button" type="button" title="重置筛选" @click="resetAll">
              <i data-lucide="rotate-ccw"></i>
            </button>
          </div>

          <div class="sport-tabs" aria-label="运动分类">
            <button
              v-for="sport in sports"
              :key="sport.id"
              :class="{ active: selectedSport === sport.id }"
              :style="{ '--accent': sport.color }"
              type="button"
              @click="goSportChannel(sport.id)"
            >
              <i :data-lucide="sport.icon"></i>
              <span>{{ sport.name }}</span>
            </button>
          </div>
        </div>

        <aside class="live-desk" aria-label="今日总览">
          <div class="desk-head">
            <span>全站热度</span>
            <b>{{ pulseScore }}</b>
          </div>
          <div class="pulse-meter" :style="{ '--pulse': pulseScore + '%' }">
            <div>
              <strong>{{ filteredNews.length }}</strong>
              <span>条内容</span>
            </div>
          </div>
          <label class="range-control">
            <span>最低热度 {{ minHeat }}</span>
            <input v-model.number="minHeat" type="range" min="0" max="100" step="5" />
          </label>
          <div class="hero-metrics">
            <span><b>{{ visibleEvents.length }}</b> 场赛程</span>
            <span><b>{{ filteredPlayers.length }}</b> 位球星</span>
            <span><b>{{ savedCount }}</b> 个收藏</span>
          </div>
        </aside>
      </section>

      <section v-if="isHomeRoute" class="ticker" aria-label="热点滚动">
        <div class="ticker-track">
          <span v-for="item in tickerItems" :key="item">{{ item }}</span>
        </div>
      </section>

      <section v-if="!isHomeRoute" class="route-hero" :class="{ 'route-hero-detail': isDetailRoute }" :style="routeHeroStyle">
        <nav class="breadcrumbs" aria-label="面包屑">
          <router-link to="/">首页</router-link>
          <span>/</span>
          <strong>{{ routeTitle }}</strong>
        </nav>
        <span class="eyebrow">{{ routeEyebrow }}</span>
        <h1>{{ routeTitle }}</h1>
        <p>{{ routeIntro }}</p>
        <div class="route-actions">
          <router-link v-for="link in routeQuickLinks" :key="link.to" :to="link.to">
            {{ link.label }}
            <i data-lucide="chevron-right"></i>
          </router-link>
        </div>
      </section>

      <section v-if="!isDetailRoute" class="filter-ribbon" aria-live="polite">
        <div class="ribbon-summary">
          <span class="eyebrow">Current View</span>
          <strong>{{ filterSummary }}</strong>
          <small>{{ resultSummary }}</small>
        </div>
        <div class="ribbon-actions">
          <button
            v-for="filter in activeFilters"
            :key="filter.id"
            type="button"
            @click="clearFilter(filter.id)"
          >
            {{ filter.label }}
            <i data-lucide="x"></i>
          </button>
          <button v-if="hasFilters" class="soft-action" type="button" @click="resetAll">清空筛选</button>
        </div>
      </section>

      <section v-if="!isDetailRoute" class="smart-lenses" aria-label="快捷视角">
        <button
          v-for="lens in smartLenses"
          :key="lens.id"
          :class="{ active: activeLens === lens.id }"
          type="button"
          @click="applyLens(lens)"
        >
          <i :data-lucide="lens.icon"></i>
          <span>{{ lens.name }}</span>
          <small>{{ lens.copy }}</small>
        </button>
      </section>

      <section v-if="showSection('leagues')" id="leagues" class="section-shell leagues-section">
        <div class="section-title">
          <div>
            <span class="eyebrow">League Hubs</span>
            <h2>联赛与赛事官方频道</h2>
          </div>
          <p>真实门户通常会把热门联赛、杯赛和巡回赛做成固定频道，这里把新闻、赛程、球队和视频串起来。</p>
        </div>

        <div class="league-grid">
          <article
            v-for="league in visibleLeagueHubs"
            :key="league.id"
            class="league-card"
            :style="{ '--accent': sportById(league.sport).color }"
          >
            <img :src="league.image" :alt="league.name" loading="lazy" />
            <div>
              <span>{{ league.organizer }} / {{ sportById(league.sport).name }}</span>
              <h3>{{ league.name }}</h3>
              <b>{{ league.window }} · {{ league.status }}</b>
              <p>{{ league.summary }}</p>
              <div class="tag-row">
                <button v-for="tag in league.tags" :key="tag" type="button" @click="goTag(tag)">{{ tag }}</button>
              </div>
              <div class="card-actions">
                <button type="button" @click="goLeague(league)">频道详情</button>
                <button type="button" @click="goSportChannel(league.sport)">项目频道</button>
              </div>
            </div>
          </article>
        </div>
      </section>

      <section v-if="routeDetail" class="section-shell detail-page" :style="{ '--accent': routeDetail.color }">
        <article class="detail-layout">
          <img :src="routeDetail.image" :alt="routeDetail.title" loading="lazy" />
          <div class="detail-copy">
            <span class="eyebrow">{{ routeDetail.eyebrow }}</span>
            <h2>{{ routeDetail.title }}</h2>
            <p>{{ routeDetail.body }}</p>
            <dl class="detail-meta">
              <div v-for="meta in routeDetail.meta" :key="meta.label">
                <dt>{{ meta.label }}</dt>
                <dd>{{ meta.value }}</dd>
              </div>
            </dl>
            <ul>
              <li v-for="point in routeDetail.points" :key="point">{{ point }}</li>
            </ul>
            <div class="detail-actions">
              <router-link v-for="link in routeDetail.links" :key="link.to" :to="link.to">
                {{ link.label }}
                <i data-lucide="chevron-right"></i>
              </router-link>
              <a v-if="routeDetail.sourceUrl" :href="routeDetail.sourceUrl" target="_blank" rel="noreferrer">
                原始来源
                <i data-lucide="external-link"></i>
              </a>
            </div>
          </div>
        </article>
        <div class="related-grid" v-if="routeRelatedLinks.length">
          <router-link v-for="link in routeRelatedLinks" :key="link.to" :to="link.to" :style="{ '--accent': link.color }">
            <span>{{ link.type }}</span>
            <b>{{ link.title }}</b>
            <small>{{ link.copy }}</small>
          </router-link>
        </div>
      </section>

      <section v-if="showSection('scores')" id="scores" class="section-shell">
        <div class="section-title">
          <div>
            <span class="eyebrow">Score Center</span>
            <h2>比分与赛况中心</h2>
          </div>
          <p>把总决赛、世界杯、WTT、F1、VNL等不同项目的赛况放在同一张工作台里。</p>
        </div>

        <div class="score-grid">
          <article
            v-for="match in visibleScoreboard"
            :key="match.id"
            class="score-card"
            :style="{ '--accent': sportById(match.sport).color }"
          >
            <div class="score-top">
              <span>{{ sportById(match.sport).name }}</span>
              <b>{{ match.status }}</b>
            </div>
            <div class="teams">
              <div>
                <button class="team-link" type="button" @click="goTeamByName(match.home)">{{ match.home }}</button>
                <strong>{{ match.homeScore }}</strong>
              </div>
              <div>
                <button class="team-link" type="button" @click="goTeamByName(match.away)">{{ match.away }}</button>
                <strong>{{ match.awayScore }}</strong>
              </div>
            </div>
            <p>{{ match.note }}</p>
            <button type="button" @click="goMatch(match)">赛况详情</button>
          </article>
        </div>
      </section>

      <section v-if="showSection('live')" id="live" class="section-shell live-section">
        <div class="section-title">
          <div>
            <span class="eyebrow">Live Desk</span>
            <h2>直播中心</h2>
          </div>
          <p>把正在直播、即将开打和适合中文时区观看的比赛集中成一张直播台。</p>
        </div>

        <div class="live-grid">
          <article
            v-for="item in liveCards"
            :key="item.id"
            class="live-card"
            :style="{ '--accent': item.color }"
          >
            <div>
              <span>{{ item.sportName }}</span>
              <b>{{ item.time }}</b>
            </div>
            <h3>{{ item.title }}</h3>
            <p>{{ item.note }}</p>
            <div class="tag-row">
              <button v-for="team in item.teamLinks" :key="team.id" type="button" @click="goTeam(team)">
                {{ team.name }}
              </button>
            </div>
            <div class="card-actions">
              <button type="button" @click="goEvent(item)">直播详情</button>
              <router-link to="/watch">加入观赛清单</router-link>
            </div>
          </article>
        </div>
      </section>

      <section v-if="showSection('calendar')" id="calendar" class="section-shell calendar-section">
        <div class="section-title">
          <div>
            <span class="eyebrow">Calendar</span>
            <h2>赛程日历</h2>
          </div>
          <p>用日期路由组织赛程，用户可以直接分享某一天的观赛安排。</p>
        </div>

        <div class="calendar-tabs">
          <router-link to="/calendar">全部日期</router-link>
          <router-link v-for="date in calendarDates" :key="date" :to="`/calendar/${date}`">{{ date }}</router-link>
        </div>

        <div class="calendar-list">
          <article
            v-for="event in calendarEvents"
            :key="event.id"
            class="calendar-card"
            :style="{ '--accent': sportById(event.sport).color }"
          >
            <strong>{{ event.date }}</strong>
            <div>
              <span>{{ sportById(event.sport).name }} · {{ event.time }}</span>
              <h3>{{ event.title }}</h3>
              <p>{{ event.place }} · {{ event.watch }}</p>
            </div>
            <div class="card-actions">
              <button type="button" @click="goEvent(event)">赛事详情</button>
              <router-link :to="`/sports/${event.sport}`">项目频道</router-link>
            </div>
          </article>
        </div>
      </section>

      <section v-if="showSection('features')" id="features" class="section-shell feature-section">
        <div class="section-title">
          <div>
            <span class="eyebrow">Special Desk</span>
            <h2>赛事专题大厅</h2>
          </div>
          <p>把正在发生和即将发生的体育主线做成专题入口，方便按赛事而不是按零散新闻浏览。</p>
        </div>

        <div class="feature-layout" v-if="visibleFeaturePacks.length">
          <article
            v-for="pack in visibleFeaturePacks"
            :key="pack.id"
            class="feature-pack"
            :style="{ '--accent': sportById(pack.sport).color }"
          >
            <img :src="sportById(pack.sport).image" :alt="pack.title" loading="lazy" />
            <div>
              <span>{{ sportById(pack.sport).name }}</span>
              <h3>{{ pack.title }}</h3>
              <p>{{ pack.summary }}</p>
              <dl>
                <div v-for="metric in pack.metrics" :key="metric.label">
                  <dt>{{ metric.label }}</dt>
                  <dd>{{ metric.value }}</dd>
                </div>
              </dl>
              <div class="card-actions">
                <button type="button" @click="goTopic(pack)">专题详情</button>
                <button type="button" @click="goSportChannel(pack.sport)">项目频道</button>
              </div>
            </div>
          </article>
        </div>
        <div v-else class="empty-state">
          <i data-lucide="inbox"></i>
          <strong>当前项目暂无专题卡片</strong>
          <span>换一个项目，或清空筛选查看全部专题。</span>
        </div>
      </section>

      <section v-if="showSection('guides')" id="guides" class="section-shell guide-section">
        <div class="section-title">
          <div>
            <span class="eyebrow">Guide Book</span>
            <h2>观赛指南与项目速查</h2>
          </div>
          <p>把不同运动的观看路径拆成容易理解的清单，新用户也能快速进入比赛语境。</p>
        </div>

        <div class="guide-grid">
          <article
            v-for="guide in visibleGuides"
            :key="guide.id"
            class="guide-card"
            :style="{ '--accent': sportById(guide.sport).color }"
          >
            <div class="guide-top">
              <span>{{ sportById(guide.sport).name }}</span>
              <b>{{ guide.level }}</b>
            </div>
            <h3>{{ guide.title }}</h3>
            <p>{{ guide.summary }}</p>
              <ul>
                <li v-for="item in guide.items" :key="item">{{ item }}</li>
              </ul>
              <div class="card-actions">
                <button type="button" @click="goGuide(guide)">站内指南</button>
                <a :href="guide.sourceUrl" target="_blank" rel="noreferrer">
                  官方资料
                  <i data-lucide="external-link"></i>
                </a>
              </div>
          </article>
        </div>
      </section>

      <section v-if="showSection('news')" id="news" class="section-shell news-section">
        <div class="section-title">
          <div>
            <span class="eyebrow">Newsroom</span>
            <h2>最新新闻与深度摘要</h2>
          </div>
          <div class="view-switch" aria-label="内容视图">
            <button :class="{ active: activeView === 'all' }" type="button" @click="activeView = 'all'">全部</button>
            <button :class="{ active: activeView === 'breaking' }" type="button" @click="activeView = 'breaking'">快讯</button>
            <button :class="{ active: activeView === 'feature' }" type="button" @click="activeView = 'feature'">专题</button>
            <button :class="{ active: activeView === 'analysis' }" type="button" @click="activeView = 'analysis'">分析</button>
          </div>
        </div>

        <div class="news-layout">
          <transition-group v-if="filteredNews.length" name="card-fade" tag="div" class="lead-stack">
            <article
              v-for="item in filteredNews"
              :key="item.id"
              class="story-card"
              :class="{ selected: selectedStory && selectedStory.id === item.id }"
              :style="{ '--accent': sportById(item.sport).color }"
            >
              <img :src="item.image" :alt="item.title" loading="lazy" />
              <div class="story-copy">
                <div class="story-meta">
                  <span>{{ sportById(item.sport).name }}</span>
                  <time>{{ item.date }}</time>
                  <b>{{ item.heat }}</b>
                </div>
                <h3>{{ item.title }}</h3>
                <p>{{ item.summary }}</p>
                  <div class="tag-row">
                    <button v-for="tag in item.tags" :key="tag" type="button" @click="goTag(tag)">{{ tag }}</button>
                </div>
                <div class="card-actions">
                    <button type="button" @click="goStory(item)">
                    <i data-lucide="panel-right-open"></i>
                    详情
                  </button>
                  <button type="button" @click="toggleSaved(item.id)">
                    <i :data-lucide="isSaved(item.id) ? 'bookmark-check' : 'bookmark'"></i>
                    {{ isSaved(item.id) ? '已收藏' : '收藏' }}
                  </button>
                </div>
              </div>
            </article>
          </transition-group>
          <div v-else class="empty-state">
            <i data-lucide="search-x"></i>
            <strong>没有找到匹配内容</strong>
            <span>试着降低热度门槛、切换项目，或清空搜索词。</span>
            <button type="button" @click="resetAll">回到全场</button>
          </div>

          <aside class="briefing-panel">
            <div class="panel-block">
              <span class="eyebrow">Reading List</span>
              <h3>收藏夹</h3>
              <p v-if="!savedStories.length">暂无收藏内容。</p>
              <button
                v-for="item in savedStories"
                :key="item.id"
                class="mini-link"
                type="button"
                  @click="goStory(item)"
              >
                <span>{{ sportById(item.sport).name }}</span>
                <b>{{ item.title }}</b>
              </button>
            </div>

            <div class="panel-block">
              <span class="eyebrow">Topic Radar</span>
              <h3>热点主题</h3>
              <button
                v-for="trend in trends"
                :key="trend.id"
                class="trend-row"
                :style="{ '--accent': sportById(trend.sport).color, '--w': trend.score + '%' }"
                type="button"
                  @click="goSportChannel(trend.sport)"
              >
                <span>{{ trend.label }}</span>
                <b>{{ trend.score }}</b>
              </button>
            </div>
          </aside>
        </div>
      </section>

      <section v-if="showSection('tactics')" class="section-shell tactics-section">
        <div class="section-title">
          <div>
            <span class="eyebrow">Tactics Board</span>
            <h2>战术拆解板</h2>
          </div>
          <p>点击左侧看点，右侧即时切换成完整解释，让阅读像看赛前战术板一样自然。</p>
        </div>

        <div class="tactics-layout">
          <div class="tactic-list">
            <button
              v-for="tactic in visibleTactics"
              :key="tactic.id"
              :class="{ active: activeTactic && activeTactic.id === tactic.id }"
              :style="{ '--accent': sportById(tactic.sport).color }"
              type="button"
              @click="activeTactic = tactic"
            >
              <span>{{ tactic.label }}</span>
              <b>{{ tactic.title }}</b>
            </button>
          </div>

          <article class="tactic-panel" v-if="activeTactic" :style="{ '--accent': sportById(activeTactic.sport).color }">
            <span class="eyebrow">{{ sportById(activeTactic.sport).name }} / {{ activeTactic.metric }}</span>
            <h3>{{ activeTactic.title }}</h3>
            <p>{{ activeTactic.summary }}</p>
            <div class="tactic-points">
              <span v-for="point in activeTactic.bullets" :key="point">{{ point }}</span>
            </div>
          </article>
        </div>
      </section>

      <section v-if="showSection('events')" id="events" class="section-shell event-section">
        <div class="section-title">
          <div>
            <span class="eyebrow">Event Library</span>
            <h2>赛事库与观赛日历</h2>
          </div>
          <div class="view-switch" aria-label="观赛时段">
            <button
              v-for="slot in timeSlots"
              :key="slot.id"
              :class="{ active: selectedSlot === slot.id }"
              type="button"
              @click="selectedSlot = slot.id"
            >
              {{ slot.name }}
            </button>
          </div>
        </div>

        <div class="event-layout">
          <div class="event-list" v-if="visibleEvents.length">
            <button
              v-for="event in visibleEvents"
              :key="event.id"
              class="event-row"
              :class="{ active: activeEvent && activeEvent.id === event.id }"
              :style="{ '--accent': sportById(event.sport).color }"
              type="button"
              @click="activeEvent = event"
            >
              <span class="event-date">{{ event.date }}</span>
              <span class="event-dot"></span>
              <span class="event-main">
                <b>{{ event.title }}</b>
                <small>{{ event.time }} · {{ event.place }}</small>
              </span>
              <i data-lucide="chevron-right"></i>
            </button>
          </div>
          <div v-else class="empty-state">
            <i data-lucide="calendar-x"></i>
            <strong>这个时段暂无赛程</strong>
            <span>切换观赛时段，或回到全部赛程。</span>
            <button type="button" @click="selectedSlot = 'all'">查看全部时段</button>
          </div>

          <article class="event-preview" v-if="activeEvent" :style="{ '--accent': sportById(activeEvent.sport).color }">
            <img :src="sportById(activeEvent.sport).image" :alt="activeEvent.title" loading="lazy" />
            <div>
              <span class="eyebrow">{{ sportById(activeEvent.sport).name }}</span>
              <h3>{{ activeEvent.title }}</h3>
              <p>{{ activeEvent.note }}</p>
              <dl class="info-grid">
                <div>
                  <dt>时间</dt>
                  <dd>{{ activeEvent.time }}</dd>
                </div>
                <div>
                  <dt>地点</dt>
                  <dd>{{ activeEvent.place }}</dd>
                </div>
                <div>
                  <dt>看点</dt>
                  <dd>{{ activeEvent.watch }}</dd>
                </div>
              </dl>
              <div class="card-actions">
                <button type="button" @click="goEvent(activeEvent)">
                  站内详情
                  <i data-lucide="chevron-right"></i>
                </button>
                <a :href="activeEvent.sourceUrl" target="_blank" rel="noreferrer">
                  查看赛程来源
                  <i data-lucide="external-link"></i>
                </a>
              </div>
            </div>
          </article>
        </div>
      </section>

      <section v-if="showSection('cities')" class="section-shell city-section">
        <div class="section-title">
          <div>
            <span class="eyebrow">Host Cities</span>
            <h2>场馆与城市故事</h2>
          </div>
          <p>赛事不只发生在比分里，城市、场馆和时区决定了观赛体验。</p>
        </div>

        <div class="venue-grid">
          <article
            v-for="venue in visibleVenues"
            :key="venue.id"
            class="venue-card"
            :style="{ '--accent': sportById(venue.sport).color }"
          >
            <img :src="venue.image" :alt="venue.name" loading="lazy" />
            <div>
              <span>{{ venue.city }} / {{ sportById(venue.sport).name }}</span>
              <h3>{{ venue.name }}</h3>
              <b>{{ venue.role }}</b>
              <p>{{ venue.note }}</p>
              <div class="card-actions">
                <button type="button" @click="goVenue(venue)">场馆详情</button>
                <button type="button" @click="goSportChannel(venue.sport)">项目频道</button>
              </div>
            </div>
          </article>
        </div>
      </section>

      <section v-if="showSection('rankings')" id="rankings" class="section-shell standings-section">
        <div class="section-title">
          <div>
            <span class="eyebrow">Rank Room</span>
            <h2>积分与排名态势</h2>
          </div>
          <p>按项目整理当前阶段最关键的排名、系列赛、冠军走势和赛程压力。</p>
        </div>

        <div class="standings-grid">
          <article
            v-for="table in visibleStandings"
            :key="table.id"
            class="standing-card"
            :style="{ '--accent': sportById(table.sport).color }"
          >
            <div class="standing-head">
              <span>{{ sportById(table.sport).name }}</span>
              <b>{{ table.title }}</b>
            </div>
            <div class="standing-row" v-for="row in table.rows" :key="row.name">
              <strong>{{ row.rank }}</strong>
              <span>{{ row.name }}</span>
              <b>{{ row.value }}</b>
            </div>
            <p>{{ table.note }}</p>
          </article>
        </div>
      </section>

      <section v-if="showSection('watch')" class="section-shell watch-section">
        <div class="section-title">
          <div>
            <span class="eyebrow">Watch List</span>
            <h2>我的观赛清单</h2>
          </div>
          <p>可勾选的观赛任务会保留在本机浏览器里，适合按时间安排接下来要看的比赛。</p>
        </div>

        <div class="watch-layout">
          <div class="watch-progress">
            <span>完成度</span>
            <b>{{ taskProgress }}%</b>
            <div :style="{ '--w': taskProgress + '%' }"><i></i></div>
          </div>
          <div class="watch-list">
            <label
              v-for="task in visibleWatchTasks"
              :key="task.id"
              class="watch-item"
              :class="{ done: checkedTasks.indexOf(task.id) !== -1 }"
              :style="{ '--accent': sportById(task.sport).color }"
            >
              <input v-model="checkedTasks" type="checkbox" :value="task.id" />
              <span>
                <b>{{ task.time }} · {{ task.title }}</b>
                <small>{{ task.detail }}</small>
              </span>
              <em>{{ task.priority }}</em>
            </label>
          </div>
        </div>
      </section>

      <section v-if="showSection('players')" id="players" class="section-shell">
        <div class="section-title">
          <div>
            <span class="eyebrow">Athlete Focus</span>
            <h2>运动员聚焦</h2>
          </div>
          <p>用状态、关键数据和近期剧情快速识别每个项目最值得关注的人。</p>
        </div>

        <div class="player-grid">
          <article
            v-for="player in filteredPlayers"
            :key="player.id"
            class="player-card"
            :style="{ '--accent': sportById(player.sport).color }"
          >
            <img :src="player.image" :alt="player.name" loading="lazy" />
            <div class="player-copy">
              <span>{{ sportById(player.sport).name }}</span>
              <h3>{{ player.name }}</h3>
              <p>{{ player.role }}</p>
              <div class="stat-row">
                <b v-for="stat in player.stats" :key="stat.label">
                  {{ stat.value }}
                  <small>{{ stat.label }}</small>
                </b>
              </div>
              <div class="player-actions">
                <button type="button" @click="goPlayer(player)">球员主页</button>
                <button type="button" @click="selectPlayer(player)">加入对比</button>
              </div>
            </div>
          </article>
        </div>
      </section>

      <section v-if="showSection('teams')" id="teams" class="section-shell teams-section">
        <div class="section-title">
          <div>
            <span class="eyebrow">Team Index</span>
            <h2>球队与阵容资料库</h2>
          </div>
          <p>把比分和赛事里出现的球队、国家队、赛事阵容做成可进入的资料页。</p>
        </div>

        <div class="team-grid">
          <article
            v-for="team in visibleTeams"
            :key="team.id"
            class="team-card"
            :style="{ '--accent': sportById(team.sport).color }"
          >
            <img :src="team.image" :alt="team.name" loading="lazy" />
            <div>
              <span>{{ sportById(team.sport).name }} / {{ team.type }}</span>
              <h3>{{ team.name }}</h3>
              <b>{{ team.city }} · {{ team.form }}</b>
              <p>{{ team.summary }}</p>
              <div class="tag-row">
                <button v-for="tag in team.tags" :key="tag" type="button" @click="goTag(tag)">{{ tag }}</button>
              </div>
              <button type="button" @click="goTeam(team)">资料页</button>
            </div>
          </article>
        </div>
      </section>

      <section v-if="showSection('index')" id="index" class="section-shell index-section">
        <div class="section-title">
          <div>
            <span class="eyebrow">Index</span>
            <h2>项目资料索引</h2>
          </div>
          <p>每个项目都有固定入口，包含当前主线、观赛窗口、代表人物和后续更新方向。</p>
        </div>

        <div class="index-grid">
          <button
            v-for="card in sportIndex"
            :key="card.id"
            type="button"
            :style="{ '--accent': sportById(card.sport).color }"
            @click="goSportChannel(card.sport)"
          >
            <span>{{ sportById(card.sport).name }}</span>
            <b>{{ card.title }}</b>
            <small>{{ card.copy }}</small>
          </button>
        </div>
      </section>

      <section v-if="showSection('data')" id="data" class="section-shell data-section">
        <div class="section-title">
          <div>
            <span class="eyebrow">Data Lab</span>
            <h2>数据实验室</h2>
          </div>
          <p>热度、赛程密度、运动员状态和跨项目对比集中呈现。</p>
        </div>

        <div class="data-layout">
          <div class="matrix-board">
            <div
              v-for="sport in sportMetrics"
              :key="sport.id"
              class="matrix-row"
              :style="{ '--accent': sport.color, '--w': sport.heat + '%' }"
            >
              <span>{{ sport.name }}</span>
              <div><i></i></div>
              <b>{{ sport.heat }}</b>
              <small>{{ sport.count }}条内容</small>
            </div>
          </div>

          <div class="compare-panel">
            <span class="eyebrow">Compare</span>
            <h3>球星对比台</h3>
            <div class="compare-selects">
              <select v-model="compareA">
                <option v-for="player in players" :key="player.id" :value="player.id">{{ player.name }}</option>
              </select>
              <select v-model="compareB">
                <option v-for="player in players" :key="player.id" :value="player.id">{{ player.name }}</option>
              </select>
            </div>
            <div class="compare-grid">
              <div v-for="player in comparisonPlayers" :key="player.id">
                <b>{{ player.name }}</b>
                <span>{{ sportById(player.sport).name }}</span>
                <p>{{ player.role }}</p>
                <div v-for="stat in player.stats" :key="stat.label" class="compare-stat">
                  <small>{{ stat.label }}</small>
                  <strong>{{ stat.value }}</strong>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section v-if="showSection('gallery')" class="section-shell gallery-section">
        <div class="section-title">
          <div>
            <span class="eyebrow">Visual Stories</span>
            <h2>图片故事墙</h2>
          </div>
          <p>同一批新闻数据自动生成视觉入口，适合快速扫项目气质和主线。</p>
        </div>

        <div class="gallery-grid">
          <button
            v-for="item in galleryStories"
            :key="item.id"
            type="button"
            :style="{ '--accent': sportById(item.sport).color }"
            @click="goStory(item)"
          >
            <img :src="item.image" :alt="item.title" loading="lazy" />
            <span>{{ sportById(item.sport).name }}</span>
            <b>{{ item.title }}</b>
          </button>
        </div>
      </section>

      <section v-if="showSection('videos')" id="videos" class="section-shell videos-section">
        <div class="section-title">
          <div>
            <span class="eyebrow">Video Hub</span>
            <h2>视频频道</h2>
          </div>
          <p>用短视频卡片承载赛前板、技术拆解和专题影像，模拟真实门户的视频频道。</p>
        </div>

        <div class="video-grid">
          <button
            v-for="video in visibleVideos"
            :key="video.id"
            type="button"
            class="video-card"
            :style="{ '--accent': sportById(video.sport).color }"
            @click="goVideo(video)"
          >
            <img :src="video.image" :alt="video.title" loading="lazy" />
            <span>{{ video.label }} · {{ video.duration }}</span>
            <b>{{ video.title }}</b>
            <small>{{ video.summary }}</small>
          </button>
        </div>
      </section>

      <section v-if="showSection('newsletter')" id="newsletter" class="section-shell newsletter-section">
        <div class="section-title">
          <div>
            <span class="eyebrow">Newsletter</span>
            <h2>订阅中心</h2>
          </div>
          <p>真实网站会用订阅入口承接回访，这里做成静态交互，用本地提示模拟订阅成功。</p>
        </div>

        <div class="newsletter-layout">
          <div class="newsletter-panel">
            <span class="eyebrow">全场邮件</span>
            <h3>选择你想收到的体育摘要</h3>
            <p>订阅不会真的发送邮件，但会展示真实站点常见的产品结构和跳转路径。</p>
            <label class="search-box">
              <i data-lucide="search"></i>
              <input v-model.trim="newsletterEmail" type="email" placeholder="输入邮箱，例如 sports@example.com" />
            </label>
          </div>
          <div class="newsletter-grid">
            <button
              v-for="plan in newsletterPlans"
              :key="plan.id"
              type="button"
              :class="{ active: subscribedPlan === plan.id }"
              @click="subscribePlan(plan)"
            >
              <span>{{ plan.tag }}</span>
              <b>{{ plan.title }}</b>
              <small>{{ plan.copy }}</small>
            </button>
          </div>
        </div>
      </section>

      <section v-if="showSection('sources')" id="sources" class="section-shell sources">
        <div class="section-title">
          <div>
            <span class="eyebrow">Sources</span>
            <h2>数据与图片来源</h2>
          </div>
          <p>站内文字为中文整理摘要，所有外链均可回到原站查看完整报道或赛程。</p>
        </div>

        <div class="source-grid">
          <a v-for="source in sourceList" :key="source.url" :href="source.url" target="_blank" rel="noreferrer">
            <span>{{ source.name }}</span>
            <i data-lucide="external-link"></i>
          </a>
        </div>
      </section>
    </main>

    <footer class="site-footer">
      <div>
        <span class="eyebrow">Channels</span>
        <router-link v-for="sport in sports.filter((item) => item.id !== 'all')" :key="sport.id" :to="`/sports/${sport.id}`">
          {{ sport.name }}
        </router-link>
      </div>
      <div>
        <span class="eyebrow">Sections</span>
        <router-link to="/news">新闻频道</router-link>
        <router-link to="/live">直播中心</router-link>
        <router-link to="/videos">视频频道</router-link>
        <router-link to="/events">赛事日历</router-link>
        <router-link to="/calendar">日期赛程</router-link>
        <router-link to="/leagues">联赛中心</router-link>
        <router-link to="/watch">观赛清单</router-link>
        <router-link to="/rankings">排名态势</router-link>
      </div>
      <div>
        <span class="eyebrow">Discover</span>
        <router-link to="/tags/NBA总决赛">NBA总决赛</router-link>
        <router-link to="/tags/世界杯">世界杯</router-link>
        <router-link to="/tags/WTT">WTT</router-link>
        <router-link to="/teams">球队资料库</router-link>
        <router-link to="/cities">城市与场馆</router-link>
        <router-link to="/newsletter">订阅中心</router-link>
        <router-link to="/authors/desk-all">编辑部</router-link>
      </div>
      <div>
        <span class="eyebrow">About</span>
        <p>全场脉冲 Pro 是一个中文综合体育门户示例，使用 Vue2 脚手架、站内路由和静态数据构建。</p>
        <router-link to="/sources">查看数据来源</router-link>
      </div>
    </footer>

      <aside class="story-drawer" :class="{ open: drawerOpen }" aria-live="polite">
      <button class="drawer-close" type="button" title="关闭详情" @click="drawerOpen = false">
        <i data-lucide="x"></i>
      </button>
      <template v-if="selectedStory">
        <img :src="selectedStory.image" :alt="selectedStory.title" />
        <div class="drawer-copy">
          <span class="eyebrow">{{ sportById(selectedStory.sport).name }} / {{ selectedStory.date }}</span>
          <h2>{{ selectedStory.title }}</h2>
          <p>{{ selectedStory.detail }}</p>
          <ul>
            <li v-for="point in selectedStory.points" :key="point">{{ point }}</li>
          </ul>
          <a :href="selectedStory.sourceUrl" target="_blank" rel="noreferrer">
            查看来源：{{ selectedStory.sourceName }}
            <i data-lucide="external-link"></i>
          </a>
        </div>
      </template>
    </aside>
    <div class="drawer-mask" v-if="drawerOpen" @click="drawerOpen = false"></div>

    <div class="floating-dock" :class="{ active: showBackTop }" aria-label="快捷操作">
      <router-link to="/search" title="全站搜索">
        <i data-lucide="search"></i>
        <span>搜索</span>
      </router-link>
      <router-link to="/calendar" title="赛程日历">
        <i data-lucide="clock-3"></i>
        <span>赛程</span>
      </router-link>
      <router-link to="/watch" title="观赛清单">
        <i data-lucide="bookmark-check"></i>
        <span>{{ savedCount || "清单" }}</span>
      </router-link>
      <button type="button" title="返回顶部" @click="scrollToTop">
        <i data-lucide="arrow-up"></i>
        <span>顶部</span>
      </button>
    </div>

    <transition name="notice-pop">
      <div class="interaction-toast" v-if="lastAction" aria-live="polite">
        <i data-lucide="sparkles"></i>
        <span>{{ lastAction }}</span>
      </div>
    </transition>
  </div>
</template>

<script>
import {
  Activity,
  ArrowUp,
  Badge,
  Bookmark,
  BookmarkCheck,
  CalendarX,
  ChartNoAxesColumn,
  ChevronRight,
  Circle,
  CircleDot,
  Clock3,
  createIcons,
  ExternalLink,
  Flame,
  Goal,
  Inbox,
  LayoutDashboard,
  PanelRightOpen,
  RotateCcw,
  Search,
  SearchX,
  Shield,
  Sparkles,
  Timer,
  X
} from "lucide";

const lucideIcons = {
  Activity,
  ArrowUp,
  Badge,
  Bookmark,
  BookmarkCheck,
  CalendarX,
  ChartNoAxesColumn,
  ChevronRight,
  Circle,
  CircleDot,
  Clock3,
  ExternalLink,
  Flame,
  Goal,
  Inbox,
  LayoutDashboard,
  PanelRightOpen,
  RotateCcw,
  Search,
  SearchX,
  Shield,
  Sparkles,
  Timer,
  X
};

const images = {
  all: "https://images.unsplash.com/photo-1461896836934-ffe607ba8211?auto=format&fit=crop&w=1600&q=80",
  basketball: "https://images.unsplash.com/photo-1546519638-68e109498ffc?auto=format&fit=crop&w=1400&q=80",
  football: "https://images.unsplash.com/photo-1579952363873-27f3bade9f55?auto=format&fit=crop&w=1400&q=80",
  tabletennis: "https://images.unsplash.com/photo-1611251135345-18c56206b863?auto=format&fit=crop&w=1400&q=80",
  tennis: "https://images.unsplash.com/photo-1622279457486-62dcc4a431d6?auto=format&fit=crop&w=1400&q=80",
  f1: "https://images.unsplash.com/photo-1503736334956-4c8f8e92946d?auto=format&fit=crop&w=1400&q=80",
  athletics: "https://images.unsplash.com/photo-1461896836934-ffe607ba8211?auto=format&fit=crop&w=1400&q=80",
  volleyball: "https://images.unsplash.com/photo-1612872087720-bb876e2e67d1?auto=format&fit=crop&w=1400&q=80"
};

const sports = [
  { id: "all", name: "全场", icon: "layout-dashboard", color: "#171717", image: images.all, intro: "从比分、新闻、赛程、运动员、数据和图片故事六个入口同时观察全球体坛。" },
  { id: "basketball", name: "篮球", icon: "circle-dot", color: "#d94a38", image: images.basketball, intro: "聚焦NBA总决赛、关键球员和系列赛走势。" },
  { id: "football", name: "足球", icon: "goal", color: "#2f7d57", image: images.football, intro: "追踪2026世界杯揭幕战、开幕式和主办城市故事。" },
  { id: "tabletennis", name: "乒乓球", icon: "badge", color: "#b33f62", image: images.tabletennis, intro: "关注WTT大满贯、资格赛排期和职业巡回赛热度。" },
  { id: "tennis", name: "网球", icon: "circle", color: "#8a6f22", image: images.tennis, intro: "整理法网冠军、新星突破和大满贯后续叙事。" },
  { id: "f1", name: "F1", icon: "timer", color: "#3b7ca6", image: images.f1, intro: "看摩纳哥赛果、车手状态和巴塞罗那下一站悬念。" },
  { id: "athletics", name: "田径", icon: "activity", color: "#6c5ce7", image: images.athletics, intro: "用钻石联赛表现串联短跑、跨栏和赛季指标。" },
  { id: "volleyball", name: "排球", icon: "shield", color: "#e07a2f", image: images.volleyball, intro: "跟进VNL赛程、主办城市和中国时区观赛窗口。" }
];

const news = [
  {
    id: "nba-spurs-g3",
    type: "breaking",
    sport: "basketball",
    date: "2026-06-09",
    title: "文班亚马抢下总决赛首胜，马刺115-111追回一城",
    summary: "AP报道，马刺在纽约赢下G3，把尼克斯的系列赛领先改写为2-1，文班亚马末节得分撑住了主队反扑。",
    detail: "马刺把防守覆盖范围重新拉满，文班亚马在关键时刻连续影响出手，系列赛从单边领先变成真正拉锯。尼克斯仍握有优势，但G4的压力明显变大。",
    points: ["系列赛来到2-1，G4仍在纽约进行。", "文班亚马末节关键得分，内线威慑改变尼克斯进攻选择。", "尼克斯需要重新提高外线命中率和转换防守质量。"],
    tags: ["NBA总决赛", "尼克斯", "马刺"],
    heat: 98,
    image: images.basketball,
    sourceName: "Associated Press",
    sourceUrl: "https://apnews.com/article/c4229e24d8254eca7125de7137f50ab7"
  },
  {
    id: "nba-finals-g4",
    type: "analysis",
    sport: "basketball",
    date: "2026-06-09",
    title: "总决赛G4临近：纽约能否守住系列赛主动权",
    summary: "NBA官方总决赛页面继续更新尼克斯对马刺系列赛，下一场将决定纽约主场优势是否还能延续。",
    detail: "G4的关键是节奏。尼克斯需要布伦森主导半场进攻并减少禁区强投，马刺则会继续用长阵容压迫传球线路。",
    points: ["G4北京时间6月11日上午进行。", "尼克斯仍以2-1领先。", "马刺如果再赢，系列赛心理重心会明显改变。"],
    tags: ["赛程", "G4", "纽约主场"],
    heat: 91,
    image: images.basketball,
    sourceName: "NBA.com",
    sourceUrl: "https://www.nba.com/playoffs/2026/nba-finals"
  },
  {
    id: "fifa-opening-match",
    type: "feature",
    sport: "football",
    date: "2026-06-09",
    title: "世界杯揭幕战倒计时：墨西哥城体育场三度迎来开幕",
    summary: "FIFA赛程信息显示，2026世界杯将于6月11日在墨西哥城开幕，揭幕战为墨西哥对南非。",
    detail: "48队、三国共同主办、16座城市，2026世界杯的规模本身就是故事。墨西哥城体育场第三次承办揭幕战，也让开幕夜带上强烈历史感。",
    points: ["比赛从6月11日持续到7月19日。", "揭幕战为墨西哥对南非。", "中文观众需要关注北京时间凌晨观赛窗口。"],
    tags: ["世界杯", "墨西哥", "南非"],
    heat: 96,
    image: images.football,
    sourceName: "FIFA",
    sourceUrl: "https://www.fifa.com/en/articles/opening-ceremony-mexico"
  },
  {
    id: "fifa-dai-dai",
    type: "feature",
    sport: "football",
    date: "2026-06-09",
    title: "Shakira与Burna Boy将在墨西哥开幕式表演官方歌曲",
    summary: "FIFA宣布，Shakira和Burna Boy将在墨西哥城开幕式上带来官方歌曲《Dai Dai》的舞台首演。",
    detail: "世界杯开幕式把音乐、城市和比赛合并成大型传播事件。墨西哥、加拿大和美国的开幕活动都会扩大赛事第一周声量。",
    points: ["墨西哥城开幕式将在揭幕战前进行。", "阵容覆盖拉美、非洲和全球流行音乐。", "足球内容不再只围绕90分钟比赛。"],
    tags: ["开幕式", "官方歌曲", "FIFA"],
    heat: 88,
    image: images.football,
    sourceName: "Inside FIFA",
    sourceUrl: "https://inside.fifa.com/organisation/media-releases/shakira-burna-dai-dai-world-cup-opening-ceremony-mexico-city"
  },
  {
    id: "wtt-us-smash",
    type: "feature",
    sport: "tabletennis",
    date: "2026-06-09",
    title: "US Smash 2026确定落地南加州安大略",
    summary: "WTT确认，US Smash 2026将于6月26日至7月5日在南加州Ontario Convention Center举行。",
    detail: "US Smash是WTT在北美市场的重要站点，积分、奖金和曝光都更接近顶级赛规格。资格赛到决赛持续十天，内容密度足够支撑完整专题。",
    points: ["比赛地点位于南加州安大略。", "赛事周期覆盖10天。", "北美继续成为WTT扩展职业影响力的重要区域。"],
    tags: ["WTT", "US Smash", "大满贯"],
    heat: 86,
    image: images.tabletennis,
    sourceName: "World Table Tennis",
    sourceUrl: "https://worldtabletennis.com/description?artId=5968"
  },
  {
    id: "wtt-qualifying",
    type: "analysis",
    sport: "tabletennis",
    date: "2026-06-09",
    title: "US Smash资格赛排期出炉，6月26日先打两节",
    summary: "票务和赛程页显示，US Smash首日安排男单、女单资格赛第一轮，分上午和傍晚两个场次。",
    detail: "资格赛日程对现场观众很友好，一天两节、节奏紧凑，也更容易看到多名球员连续登场。对中国球迷来说，时差会是观赛难点。",
    points: ["当地6月26日10:00开启资格赛。", "当地6月26日17:30安排第二节。", "6月28日起进入单打64强和双打阶段。"],
    tags: ["资格赛", "赛程", "安大略"],
    heat: 77,
    image: images.tabletennis,
    sourceName: "Koobit",
    sourceUrl: "https://www.koobit.com/united-states-smash-e55730"
  },
  {
    id: "tennis-zverev",
    type: "breaking",
    sport: "tennis",
    date: "2026-06-08",
    title: "兹维列夫五盘赢下法网男单，终于解锁首个大满贯",
    summary: "法网报道，兹维列夫在决赛击败科博利，完成职业生涯重要突破。",
    detail: "兹维列夫的巴黎胜利改变了男网叙事：他不再只是稳定争冠者，而是大满贯冠军。草地赛季马上到来，体能恢复和心理释放都很关键。",
    points: ["决赛打满五盘。", "科博利首次进入大满贯决赛。", "男网格局多了新冠军变量。"],
    tags: ["法网", "兹维列夫", "大满贯"],
    heat: 84,
    image: images.tennis,
    sourceName: "Roland-Garros",
    sourceUrl: "https://www.rolandgarros.com/en-us/article/2026-edition-zverev-cobolli-final-report"
  },
  {
    id: "tennis-andreeva",
    type: "feature",
    sport: "tennis",
    date: "2026-06-07",
    title: "安德列娃19岁夺法网女单，年轻冠军线再次被刷新",
    summary: "Euronews报道，安德列娃在女单决赛中击败资格赛黑马赫瓦林斯卡，拿下个人首个大满贯。",
    detail: "安德列娃的夺冠让女子网坛继续年轻化。赫瓦林斯卡从资格赛打进决赛同样是巴黎最有戏剧性的故事之一。",
    points: ["安德列娃直落两盘赢下决赛。", "赫瓦林斯卡制造资格赛黑马叙事。", "草地与硬地稳定性会成为后续观察点。"],
    tags: ["法网", "安德列娃", "女单"],
    heat: 82,
    image: images.tennis,
    sourceName: "Euronews",
    sourceUrl: "https://www.euronews.com/2026/06/06/tennis-mirra-andreeva-wins-womens-roland-garros-final"
  },
  {
    id: "f1-monaco-result",
    type: "breaking",
    sport: "f1",
    date: "2026-06-08",
    title: "安东内利摩纳哥夺冠，汉密尔顿第二",
    summary: "F1官方成绩页显示，安东内利赢下2026摩纳哥大奖赛，汉密尔顿第二，哈贾尔登上领奖台。",
    detail: "摩纳哥站强化了安东内利的赛季统治力。街道赛对排位和稳定性要求极高，他把杆位优势转成胜势。",
    points: ["安东内利78圈完赛夺冠。", "汉密尔顿获得第二。", "下一站巴塞罗那更考验赛车综合速度。"],
    tags: ["摩纳哥", "安东内利", "汉密尔顿"],
    heat: 90,
    image: images.f1,
    sourceName: "Formula 1",
    sourceUrl: "https://www.formula1.com/en/results/2026/races/1286/monaco/race-result"
  },
  {
    id: "f1-barcelona-next",
    type: "analysis",
    sport: "f1",
    date: "2026-06-09",
    title: "F1下一站巴塞罗那：6月12日至14日连轴开跑",
    summary: "F1官方日历显示，巴塞罗那-加泰罗尼亚大奖赛将在6月12日至14日进行，正赛定于6月14日。",
    detail: "巴塞罗那是检验赛车真实速度的经典样本。相比摩纳哥，长弯、轮胎衰退和空气动力效率会更明显地暴露车队差距。",
    points: ["巴塞罗那站是赛季第7轮。", "正赛官方页面显示为6月14日。", "车队升级包会被放到显微镜下。"],
    tags: ["巴塞罗那", "赛程", "正赛"],
    heat: 79,
    image: images.f1,
    sourceName: "Formula 1 Calendar",
    sourceUrl: "https://www.formula1.com/en/racing/2026/barcelona-catalunya"
  },
  {
    id: "athletics-rome",
    type: "analysis",
    sport: "athletics",
    date: "2026-06-05",
    title: "罗马钻石联赛：莱尔斯后程爆发，跨栏与400米栏也刷出亮点",
    summary: "钻石联赛官网报道，莱尔斯在罗马站男子100米后程发力取胜，坎宁安和扎普莱塔洛娃也跑出高质量成绩。",
    detail: "田径区的价值在于把多个项目的状态串起来看。罗马站显示，短跑、男子110米栏和女子400米栏都已进入赛季高压段。",
    points: ["莱尔斯用后程能力赢下男子100米。", "特雷-坎宁安跑出12.98。", "扎普莱塔洛娃52.58刷新赛季指标。"],
    tags: ["钻石联赛", "莱尔斯", "田径"],
    heat: 74,
    image: images.athletics,
    sourceName: "Diamond League",
    sourceUrl: "https://www.diamondleague.com/lyles-impresses-at-rome-diamond-league/"
  },
  {
    id: "vnl-2026",
    type: "feature",
    sport: "volleyball",
    date: "2026-06-09",
    title: "VNL 2026进入密集赛程，18队制让每周都有强强对话",
    summary: "FIVB公布的赛程显示，2026世界女排/男排联赛均为18队参赛，分站横跨多个大洲。",
    detail: "VNL赛制扩大之后，比赛密度和跨时区观看成本都增加。中文门户最适合把中国时区友好的场次和强强对话抽出来。",
    points: ["女排赛程6月初已在南京等赛区开启。", "男排赛程6月10日在中国临沂开局。", "18队制让分站竞争更早进入白热化。"],
    tags: ["VNL", "排球", "分站赛"],
    heat: 81,
    image: images.volleyball,
    sourceName: "FIVB",
    sourceUrl: "https://www.fivb.com/volleyball-world-reveals-2026-vnl-match-schedule/"
  },
  {
    id: "vnl-cuba-poland",
    type: "breaking",
    sport: "volleyball",
    date: "2026-06-09",
    title: "男排VNL临沂揭幕战：古巴对波兰，北京时间6月10日13:00",
    summary: "Volleyball World赛程页显示，古巴与波兰将在中国临沂进行男排VNL 2026首场比赛。",
    detail: "这场比赛对中文用户非常友好：就在中国时区进行，且由世界排名前列的波兰登场，适合放进首页赛程提醒。",
    points: ["比赛地点为中国临沂。", "官方时间为6月10日13:00。", "这是男排VNL第一周开局场次。"],
    tags: ["临沂", "波兰", "古巴"],
    heat: 80,
    image: images.volleyball,
    sourceName: "Volleyball World",
    sourceUrl: "https://en.volleyballworld.com/volleyball/competitions/volleyball-nations-league/schedule/26434/"
  }
];

const events = [
  { id: "vnl-opener", sport: "volleyball", slot: "midday", date: "06-10", time: "13:00 北京时间", place: "中国临沂", title: "男排VNL：古巴 vs 波兰", note: "男排VNL 2026第一场，波兰作为强队登场。", watch: "中国时区友好，适合午间观赛。", sourceUrl: "https://en.volleyballworld.com/volleyball/competitions/volleyball-nations-league/schedule/26434/" },
  { id: "nba-g4", sport: "basketball", slot: "morning", date: "06-11", time: "08:30 北京时间", place: "纽约", title: "NBA总决赛G4：马刺 vs 尼克斯", note: "马刺追回一城后，G4会决定系列赛是否真正进入拉锯。", watch: "布伦森挡拆选择对文班亚马护筐。", sourceUrl: "https://www.nba.com/playoffs/2026/nba-finals" },
  { id: "worldcup-open", sport: "football", slot: "night", date: "06-12", time: "03:00 北京时间", place: "墨西哥城体育场", title: "世界杯揭幕战：墨西哥 vs 南非", note: "2026世界杯第一场，墨西哥城体育场再度成为开幕地。", watch: "主队揭幕战压力与开幕式传播。", sourceUrl: "https://www.fifa.com/en/articles/opening-ceremony-mexico" },
  { id: "tennis-grass", sport: "tennis", slot: "evening", date: "06-12", time: "欧洲晚间", place: "草地赛季前哨", title: "网球草地赛季观察窗口", note: "法网结束后，球员从红土转向草地，发球后一拍和恢复质量会先被检验。", watch: "新冠军的身体恢复与低弹跳处理。", sourceUrl: "https://www.rolandgarros.com/en-us/" },
  { id: "f1-barcelona", sport: "f1", slot: "evening", date: "06-14", time: "21:00 北京时间", place: "巴塞罗那-加泰罗尼亚赛道", title: "F1巴塞罗那大奖赛正赛", note: "从街道赛切到传统赛道，车队升级会被全面检验。", watch: "轮胎管理、长弯速度和升级包效果。", sourceUrl: "https://www.formula1.com/en/racing/2026/barcelona-catalunya" },
  { id: "us-smash-start", sport: "tabletennis", slot: "night", date: "06-26", time: "当地10:00", place: "Ontario Convention Center", title: "US Smash 2026资格赛首日", note: "男单和女单资格赛率先开打，完整比赛持续到7月5日。", watch: "北美大满贯站的资格赛强度。", sourceUrl: "https://worldtabletennis.com/description?artId=5968" },
  { id: "diamond-next", sport: "athletics", slot: "night", date: "06-15", time: "欧洲晚间", place: "钻石联赛赛场", title: "钻石联赛后续分站", note: "罗马站后，短跑和跨栏项目进入连续观察窗口。", watch: "莱尔斯后程状态和栏间节奏。", sourceUrl: "https://www.diamondleague.com/" }
];

const scoreboard = [
  { id: "score-nba", sport: "basketball", status: "系列赛 2-1", home: "尼克斯", away: "马刺", homeScore: "2", awayScore: "1", note: "G4将在纽约继续进行，马刺已经抢回系列赛气势。" },
  { id: "score-fifa", sport: "football", status: "揭幕倒计时", home: "墨西哥", away: "南非", homeScore: "06-12", awayScore: "03:00", note: "世界杯揭幕战进入最后准备阶段。" },
  { id: "score-wtt", sport: "tabletennis", status: "报名/排期", home: "US Smash", away: "资格赛", homeScore: "06-26", awayScore: "07-05", note: "WTT北美大满贯站将连续十天进行。" },
  { id: "score-f1", sport: "f1", status: "下一站", home: "巴塞罗那", away: "正赛", homeScore: "06-14", awayScore: "21:00", note: "传统赛道检验赛车综合性能。" },
  { id: "score-vnl", sport: "volleyball", status: "揭幕战", home: "古巴", away: "波兰", homeScore: "06-10", awayScore: "13:00", note: "男排VNL从中国临沂开启第一周。" }
];

const teamProfiles = [
  { id: "knicks", sport: "basketball", name: "尼克斯", city: "纽约", type: "NBA球队", form: "系列赛2-1领先", venue: "麦迪逊广场花园", summary: "主场声量和布伦森半场进攻是G4的核心优势。", image: images.basketball, tags: ["NBA总决赛", "纽约主场", "布伦森"] },
  { id: "spurs", sport: "basketball", name: "马刺", city: "圣安东尼奥", type: "NBA球队", form: "G3追回一城", venue: "客场作战", summary: "长阵容和文班亚马护筐让系列赛重新进入拉锯。", image: images.basketball, tags: ["文班亚马", "护筐", "长阵容"] },
  { id: "mexico", sport: "football", name: "墨西哥", city: "墨西哥城", type: "国家队", form: "揭幕战主队", venue: "墨西哥城体育场", summary: "东道主开幕压力、控球节奏和城市情绪会同时进入镜头。", image: images.football, tags: ["世界杯", "东道主", "开幕"] },
  { id: "south-africa", sport: "football", name: "南非", city: "约翰内斯堡", type: "国家队", form: "揭幕战对手", venue: "墨西哥城体育场", summary: "反击效率和定位球质量是揭幕战最直接的变数。", image: images.football, tags: ["世界杯", "反击", "揭幕战"] },
  { id: "cuba-vb", sport: "volleyball", name: "古巴", city: "哈瓦那", type: "男排国家队", form: "VNL揭幕", venue: "临沂赛区", summary: "发球冲击和高点进攻会考验波兰一传稳定性。", image: images.volleyball, tags: ["VNL", "发球", "临沂"] },
  { id: "poland-vb-team", sport: "volleyball", name: "波兰", city: "华沙", type: "男排国家队", form: "强队样本", venue: "临沂赛区", summary: "首周轮换、一传到位率和副攻参与度值得重点观察。", image: images.volleyball, tags: ["VNL", "强队", "一传"] },
  { id: "us-smash", sport: "tabletennis", name: "US Smash", city: "南加州安大略", type: "WTT赛事阵容", form: "资格赛临近", venue: "Ontario Convention Center", summary: "北美大满贯站会把资格赛、正赛和双打线索放进同一窗口。", image: images.tabletennis, tags: ["WTT", "大满贯", "资格赛"] },
  { id: "barcelona-gp", sport: "f1", name: "巴塞罗那", city: "巴塞罗那", type: "F1大奖赛", form: "下一站", venue: "加泰罗尼亚赛道", summary: "传统赛道会检验长弯、轮胎衰退和升级包相关性。", image: images.f1, tags: ["F1", "升级包", "轮胎"] }
];

const players = [
  { id: "wemby", sport: "basketball", name: "维克托-文班亚马", role: "总决赛关键护筐点，末节攻防影响力持续扩大。", image: images.basketball, stats: [{ label: "热度", value: "98" }, { label: "主线", value: "护筐" }, { label: "阶段", value: "G4" }] },
  { id: "brunson", sport: "basketball", name: "杰伦-布伦森", role: "尼克斯半场进攻发动机，G4需要提升禁区外决策。", image: images.basketball, stats: [{ label: "热度", value: "91" }, { label: "主线", value: "挡拆" }, { label: "阶段", value: "领先" }] },
  { id: "zverev", sport: "tennis", name: "亚历山大-兹维列夫", role: "法网夺冠后进入职业新阶段，草地赛季关注恢复。", image: images.tennis, stats: [{ label: "热度", value: "84" }, { label: "主线", value: "首冠" }, { label: "阶段", value: "转场" }] },
  { id: "andreeva", sport: "tennis", name: "米拉-安德列娃", role: "年轻大满贯冠军，女子网坛新核心故事线。", image: images.tennis, stats: [{ label: "热度", value: "82" }, { label: "主线", value: "新星" }, { label: "阶段", value: "上升" }] },
  { id: "antonelli", sport: "f1", name: "安东内利", role: "摩纳哥夺冠后继续领跑，巴塞罗那检验统治力。", image: images.f1, stats: [{ label: "热度", value: "90" }, { label: "主线", value: "连胜" }, { label: "阶段", value: "领跑" }] },
  { id: "lyles", sport: "athletics", name: "诺亚-莱尔斯", role: "罗马站后程能力突出，短跑赛季指标继续抬升。", image: images.athletics, stats: [{ label: "热度", value: "74" }, { label: "主线", value: "后程" }, { label: "阶段", value: "分站" }] },
  { id: "poland-vb", sport: "volleyball", name: "波兰男排", role: "VNL临沂揭幕战强队样本，首周状态值得观察。", image: images.volleyball, stats: [{ label: "热度", value: "80" }, { label: "主线", value: "开局" }, { label: "阶段", value: "VNL" }] },
  { id: "us-smash-field", sport: "tabletennis", name: "US Smash种子群", role: "北美大满贯站即将启动，资格赛到正赛连续输出。", image: images.tabletennis, stats: [{ label: "热度", value: "86" }, { label: "主线", value: "大满贯" }, { label: "阶段", value: "备战" }] }
];

const trends = [
  { id: "trend-nba", sport: "basketball", label: "NBA总决赛变成2-1", reason: "马刺客场赢球，G4悬念升温", score: 98 },
  { id: "trend-worldcup", sport: "football", label: "世界杯揭幕战倒计时", reason: "墨西哥对南非进入开幕窗口", score: 96 },
  { id: "trend-f1", sport: "f1", label: "安东内利五连胜叙事", reason: "摩纳哥夺冠后看巴塞罗那", score: 90 },
  { id: "trend-wtt", sport: "tabletennis", label: "US Smash临近", reason: "南加州大满贯站开赛在即", score: 86 },
  { id: "trend-tennis", sport: "tennis", label: "法网新冠军出炉", reason: "男女单都出现关键节点", score: 84 },
  { id: "trend-vnl", sport: "volleyball", label: "VNL进入中国时区", reason: "临沂站开局观赛友好", score: 81 },
  { id: "trend-track", sport: "athletics", label: "钻石联赛指标抬升", reason: "罗马站多项目出好成绩", score: 74 }
];

const smartLenses = [
  { id: "lens-hot", name: "高热新闻", copy: "只看热度85+", icon: "flame", sport: "all", view: "all", slot: "all", heat: 85, query: "" },
  { id: "lens-live", name: "中国时区", copy: "午间赛程入口", icon: "clock-3", sport: "volleyball", view: "all", slot: "midday", heat: 0, query: "" },
  { id: "lens-cup", name: "世界杯周", copy: "开幕与城市故事", icon: "goal", sport: "football", view: "feature", slot: "night", heat: 0, query: "" },
  { id: "lens-racket", name: "小球专区", copy: "乒乓与网球线索", icon: "badge", sport: "tabletennis", view: "all", slot: "all", heat: 0, query: "" },
  { id: "lens-data", name: "数据观察", copy: "分析类内容", icon: "chart-no-axes-column", sport: "all", view: "analysis", slot: "all", heat: 0, query: "" }
];

const videoCards = [
  { id: "video-nba-g4", sport: "basketball", title: "尼克斯vs马刺G4赛前板", duration: "04:18", label: "赛前", summary: "用三分钟看懂文班亚马护筐和布伦森挡拆之间的拉扯。", image: images.basketball, related: "nba-g4" },
  { id: "video-worldcup-open", sport: "football", title: "世界杯开幕周城市镜头", duration: "05:02", label: "专题", summary: "从墨西哥城体育场、开幕式和揭幕战压力看开幕周叙事。", image: images.football, related: "worldcup-open" },
  { id: "video-wtt-smash", sport: "tabletennis", title: "US Smash资格赛路线图", duration: "03:46", label: "指南", summary: "资格赛、单打64强和北美时差如何影响中文观赛节奏。", image: images.tabletennis, related: "us-smash-start" },
  { id: "video-f1-barcelona", sport: "f1", title: "巴塞罗那为什么像一次考试", duration: "04:55", label: "技术", summary: "传统赛道会把长距离速度、轮胎窗口和升级包效果摊开。", image: images.f1, related: "f1-barcelona" },
  { id: "video-vnl-linyi", sport: "volleyball", title: "VNL临沂揭幕战观察", duration: "02:58", label: "直播前瞻", summary: "波兰一传、古巴发球和中国时区观赛窗口是首场重点。", image: images.volleyball, related: "vnl-opener" },
  { id: "video-diamond", sport: "athletics", title: "钻石联赛指标速览", duration: "03:21", label: "数据", summary: "莱尔斯后程、男子110米栏和女子400米栏的赛季参照线。", image: images.athletics, related: "diamond-next" }
];

const leagueHubs = [
  { id: "nba-finals", sport: "basketball", name: "NBA总决赛", organizer: "NBA", season: "2026 Finals", window: "6月", status: "尼克斯2-1领先", summary: "总决赛频道聚合比分、战术、球员、视频和球队资料。", image: images.basketball, tags: ["NBA总决赛", "尼克斯", "马刺"] },
  { id: "world-cup", sport: "football", name: "2026世界杯", organizer: "FIFA", season: "2026", window: "6.11-7.19", status: "揭幕战倒计时", summary: "世界杯频道覆盖开幕式、揭幕战、主办城市和国家队资料。", image: images.football, tags: ["世界杯", "墨西哥", "南非"] },
  { id: "wtt-smash", sport: "tabletennis", name: "US Smash", organizer: "WTT", season: "2026", window: "6.26-7.5", status: "资格赛临近", summary: "WTT大满贯频道追踪资格赛、正赛、双打和北美时差。", image: images.tabletennis, tags: ["WTT", "US Smash", "大满贯"] },
  { id: "f1-calendar", sport: "f1", name: "F1大奖赛", organizer: "Formula 1", season: "2026", window: "赛季第7轮", status: "巴塞罗那下一站", summary: "F1频道汇总赛程、赛道、车手状态、视频技术拆解。", image: images.f1, tags: ["F1", "巴塞罗那", "升级包"] },
  { id: "vnl", sport: "volleyball", name: "VNL世界联赛", organizer: "Volleyball World", season: "2026", window: "第一周", status: "临沂揭幕", summary: "VNL频道突出中国时区观赛、强队状态和分站赛程。", image: images.volleyball, tags: ["VNL", "临沂", "波兰"] },
  { id: "diamond-league", sport: "athletics", name: "钻石联赛", organizer: "World Athletics", season: "2026", window: "欧洲分站", status: "指标抬升", summary: "田径频道用分站表现跟踪赛季最佳、短跑和跨栏线索。", image: images.athletics, tags: ["钻石联赛", "莱尔斯", "田径"] }
];

const editorProfiles = [
  { id: "desk-all", name: "全场编辑部", role: "综合体育编辑组", beat: "新闻与赛程", summary: "负责首页快讯、跨项目专题和来源整理。", image: images.all },
  { id: "desk-tactics", name: "战术研究室", role: "技战术分析组", beat: "篮球、足球、F1", summary: "负责战术板、赛前看点和数据实验室解读。", image: images.basketball },
  { id: "desk-live", name: "直播运营台", role: "赛程与直播组", beat: "直播、清单、城市", summary: "负责中文时区赛程、直播入口和观赛清单维护。", image: images.volleyball }
];

const newsletterPlans = [
  { id: "daily", title: "每日早报", copy: "每天上午推送昨夜赛果、今日直播和三条必读新闻。", tag: "适合通勤" },
  { id: "weekend", title: "周末观赛包", copy: "每周五整理周末重点赛事、视频和球队资料入口。", tag: "适合重度观赛" },
  { id: "data", title: "数据周报", copy: "每周汇总热度、排名、球员对比和技术拆解。", tag: "适合分析党" }
];

const featurePacks = [
  {
    id: "pack-nba-finals",
    sport: "basketball",
    title: "NBA总决赛战术追踪",
    summary: "尼克斯仍握系列赛领先，但马刺在G3证明自己可以用护筐和长阵容改变比赛形态。",
    metrics: [
      { label: "系列赛", value: "尼克斯2-1" },
      { label: "下一场", value: "G4纽约" },
      { label: "核心矛盾", value: "挡拆vs护筐" }
    ]
  },
  {
    id: "pack-world-cup",
    sport: "football",
    title: "2026世界杯开幕周",
    summary: "三国共同主办、48队参赛、墨西哥城揭幕，开幕周会同时牵动比赛、城市和文娱传播。",
    metrics: [
      { label: "揭幕战", value: "墨西哥vs南非" },
      { label: "城市", value: "16座" },
      { label: "周期", value: "6.11-7.19" }
    ]
  },
  {
    id: "pack-wtt",
    sport: "tabletennis",
    title: "US Smash北美大满贯站",
    summary: "WTT在南加州连续十天开赛，资格赛、正赛、双打和混双会形成高密度内容窗口。",
    metrics: [
      { label: "开赛", value: "6月26日" },
      { label: "地点", value: "安大略" },
      { label: "周期", value: "10天" }
    ]
  },
  {
    id: "pack-tennis",
    sport: "tennis",
    title: "法网后草地转场",
    summary: "新科大满贯冠军和年轻冠军都要快速从红土节奏切到草地，发球、低弹跳和体能恢复会成为接下来两周的关键词。",
    metrics: [
      { label: "阶段", value: "法网后" },
      { label: "看点", value: "草地转场" },
      { label: "变量", value: "恢复" }
    ]
  },
  {
    id: "pack-f1",
    sport: "f1",
    title: "从摩纳哥到巴塞罗那",
    summary: "摩纳哥看排位与稳定性，巴塞罗那看综合车速和轮胎管理，两个赛道能串出车队真实水平。",
    metrics: [
      { label: "上站", value: "摩纳哥" },
      { label: "下站", value: "巴塞罗那" },
      { label: "焦点", value: "升级包" }
    ]
  },
  {
    id: "pack-vnl",
    sport: "volleyball",
    title: "VNL中国时区观赛指南",
    summary: "临沂站让男排VNL开局进入中文观众友好时段，强队状态和18队制节奏值得跟进。",
    metrics: [
      { label: "揭幕", value: "古巴vs波兰" },
      { label: "地点", value: "临沂" },
      { label: "赛制", value: "18队" }
    ]
  },
  {
    id: "pack-athletics",
    sport: "athletics",
    title: "钻石联赛赛季指标板",
    summary: "罗马站之后，短跑、110米栏和女子400米栏都有值得持续观察的赛季指标。",
    metrics: [
      { label: "短跑", value: "莱尔斯" },
      { label: "跨栏", value: "12.98" },
      { label: "400栏", value: "52.58" }
    ]
  }
];

const standings = [
  {
    id: "stand-nba",
    sport: "basketball",
    title: "总决赛系列赛",
    rows: [
      { rank: "1", name: "纽约尼克斯", value: "2胜" },
      { rank: "2", name: "圣安东尼奥马刺", value: "1胜" },
      { rank: "G4", name: "下一场", value: "纽约" }
    ],
    note: "尼克斯仍领先，但G3后系列赛主动权开始松动。"
  },
  {
    id: "stand-football",
    sport: "football",
    title: "世界杯开幕态势",
    rows: [
      { rank: "A", name: "墨西哥", value: "揭幕主队" },
      { rank: "A", name: "南非", value: "揭幕对手" },
      { rank: "48", name: "参赛队", value: "扩军版" }
    ],
    note: "开幕战不仅是比赛，也是本届世界杯传播启动点。"
  },
  {
    id: "stand-wtt",
    sport: "tabletennis",
    title: "US Smash进程",
    rows: [
      { rank: "Q", name: "资格赛", value: "6月26日" },
      { rank: "R64", name: "正赛", value: "6月28日" },
      { rank: "F", name: "决赛周", value: "7月" }
    ],
    note: "资格赛到正赛连接紧密，适合做连续追踪。"
  },
  {
    id: "stand-f1",
    sport: "f1",
    title: "大奖赛节奏",
    rows: [
      { rank: "1", name: "安东内利", value: "摩纳哥胜" },
      { rank: "2", name: "汉密尔顿", value: "摩纳哥P2" },
      { rank: "7", name: "巴塞罗那", value: "第7轮" }
    ],
    note: "传统赛道会比摩纳哥更真实地暴露赛车差异。"
  },
  {
    id: "stand-vnl",
    sport: "volleyball",
    title: "VNL第一周",
    rows: [
      { rank: "1", name: "临沂站", value: "男排开局" },
      { rank: "18", name: "参赛队", value: "新赛制" },
      { rank: "CN", name: "中文时区", value: "友好" }
    ],
    note: "临沂站是中文用户最容易切入的第一周入口。"
  },
  {
    id: "stand-track",
    sport: "athletics",
    title: "田径指标",
    rows: [
      { rank: "100", name: "莱尔斯", value: "后程强" },
      { rank: "110H", name: "坎宁安", value: "12.98" },
      { rank: "400H", name: "扎普莱塔洛娃", value: "52.58" }
    ],
    note: "钻石联赛分站成绩会不断刷新赛季参照线。"
  }
];

const sportIndex = [
  { id: "idx-basketball", sport: "basketball", title: "总决赛中心", copy: "赛程、系列赛比分、关键球员和战术变化。" },
  { id: "idx-football", sport: "football", title: "世界杯开幕周", copy: "揭幕战、开幕式、主办城市和小组阶段入口。" },
  { id: "idx-tabletennis", sport: "tabletennis", title: "WTT大满贯", copy: "资格赛排期、正赛阶段和北美站点动态。" },
  { id: "idx-tennis", sport: "tennis", title: "大满贯后续", copy: "法网冠军、年轻球员和草地赛季过渡。" },
  { id: "idx-f1", sport: "f1", title: "大奖赛追踪", copy: "上站结果、下站赛程、车队升级和车手走势。" },
  { id: "idx-athletics", sport: "athletics", title: "钻石联赛", copy: "分站成绩、赛季最佳和重点项目指标。" },
  { id: "idx-volleyball", sport: "volleyball", title: "VNL联赛", copy: "分站赛程、中国时区、强队状态和18队制。" }
];

const guideCards = [
  {
    id: "guide-nba",
    sport: "basketball",
    title: "NBA总决赛阅读指南",
    level: "季后赛终章",
    summary: "从系列赛比分、主客场切换、核心对位和轮换深度四个层面读总决赛。",
    items: ["系列赛走势", "关键球员犯规控制", "第四节阵容选择", "三分出手质量"],
    sourceName: "NBA.com",
    sourceUrl: "https://www.nba.com/playoffs/2026/nba-finals"
  },
  {
    id: "guide-worldcup",
    sport: "football",
    title: "世界杯扩军版速查",
    level: "48队时代",
    summary: "2026世界杯的核心变化是规模、城市跨度和赛程密度，开幕周会决定第一轮舆论节奏。",
    items: ["三国共同主办", "16座主办城市", "小组赛密度提升", "开幕式传播"],
    sourceName: "FIFA",
    sourceUrl: "https://www.fifa.com/en/articles/opening-ceremony-mexico"
  },
  {
    id: "guide-wtt",
    sport: "tabletennis",
    title: "WTT大满贯观赛结构",
    level: "资格赛到决赛",
    summary: "US Smash从资格赛开始铺开，单打、双打、混双会交替推进，适合做每日更新。",
    items: ["资格赛两节制", "单打64强", "双打混双并行", "北美时差"],
    sourceName: "World Table Tennis",
    sourceUrl: "https://worldtabletennis.com/description?artId=5968"
  },
  {
    id: "guide-tennis",
    sport: "tennis",
    title: "大满贯后续观察法",
    level: "法网之后",
    summary: "红土大满贯结束后，球员需要快速切换到草地节奏，体能和发球质量会变得更重要。",
    items: ["体能恢复", "发球落点", "草地移动", "新冠军压力"],
    sourceName: "Roland-Garros",
    sourceUrl: "https://www.rolandgarros.com/en-us/article/2026-edition-zverev-cobolli-final-report"
  },
  {
    id: "guide-f1",
    sport: "f1",
    title: "F1赛周拆解",
    level: "练习赛到正赛",
    summary: "传统赛道比街道赛更能检验赛车真实水平，长距离速度和轮胎衰退是核心指标。",
    items: ["自由练习长距离", "排位轮胎窗口", "正赛策略窗口", "升级包验证"],
    sourceName: "Formula 1",
    sourceUrl: "https://www.formula1.com/en/racing/2026/barcelona-catalunya"
  },
  {
    id: "guide-vnl",
    sport: "volleyball",
    title: "VNL分站赛看法",
    level: "18队制",
    summary: "VNL扩容后，第一周状态会更影响后续轮换和晋级压力，主办城市也会改变观赛便利度。",
    items: ["分站轮换", "强队首发", "中国时区", "积分压力"],
    sourceName: "Volleyball World",
    sourceUrl: "https://en.volleyballworld.com/volleyball/competitions/volleyball-nations-league/schedule/26434/"
  }
];

const tactics = [
  {
    id: "tactic-wemby",
    sport: "basketball",
    label: "禁区重力",
    title: "文班亚马如何改变尼克斯出手地图",
    summary: "马刺的关键不是每回合都封盖，而是让尼克斯在进入禁区前就开始犹豫。",
    metric: "护筐影响",
    bullets: ["弱侧协防提前站位", "逼迫中距离急停", "保护篮板后立刻推转换"]
  },
  {
    id: "tactic-mexico",
    sport: "football",
    label: "揭幕压强",
    title: "东道主揭幕战的节奏风险",
    summary: "墨西哥需要在情绪和控球之间找到平衡，过早提速可能给南非反击空间。",
    metric: "控球节奏",
    bullets: ["前15分钟控场", "边路压迫后保护身后", "定位球质量决定上半场优势"]
  },
  {
    id: "tactic-wtt",
    sport: "tabletennis",
    label: "前三板",
    title: "大满贯资格赛最容易爆冷的地方",
    summary: "资格赛里信息差更大，发接发质量和前三板变化常常比相持能力更早决定走向。",
    metric: "发接发",
    bullets: ["短球质量", "反手拧拉落点", "接发抢攻后的衔接"]
  },
  {
    id: "tactic-tennis",
    sport: "tennis",
    label: "场地切换",
    title: "红土冠军转草地的第一道坎",
    summary: "红土长回合优势不能直接搬到草地，发球后一拍和低重心移动会变得更重要。",
    metric: "转换效率",
    bullets: ["一发得分率", "网前终结", "低弹跳处理"]
  },
  {
    id: "tactic-f1",
    sport: "f1",
    label: "轮胎窗口",
    title: "巴塞罗那为什么比摩纳哥更像考试",
    summary: "摩纳哥更看排位和赛道位置，巴塞罗那会把长弯、轮胎衰退和空气动力效率都摊开。",
    metric: "长距离",
    bullets: ["中高速弯稳定性", "二停窗口", "升级包相关性"]
  },
  {
    id: "tactic-vnl",
    sport: "volleyball",
    label: "一传压力",
    title: "VNL第一周最该盯的一传质量",
    summary: "分站第一周球队磨合还没完全稳定，一传到位率会直接影响副攻参与度。",
    metric: "一传到位",
    bullets: ["强发球轮", "副攻快球比例", "二传分配节奏"]
  }
];

const venues = [
  { id: "venue-msg", sport: "basketball", name: "麦迪逊广场花园", city: "纽约", role: "总决赛主场", note: "尼克斯G4继续承压，主场声量会放大每个回合。", image: images.basketball },
  { id: "venue-azteca", sport: "football", name: "墨西哥城体育场", city: "墨西哥城", role: "世界杯揭幕地", note: "第三次承办世界杯揭幕战，历史符号很强。", image: images.football },
  { id: "venue-ontario", sport: "tabletennis", name: "Ontario Convention Center", city: "南加州安大略", role: "US Smash赛场", note: "北美大满贯站核心场馆，赛程覆盖十天。", image: images.tabletennis },
  { id: "venue-paris", sport: "tennis", name: "罗兰加洛斯", city: "巴黎", role: "法网故事源", note: "新冠军从这里进入后续草地赛季观察窗口。", image: images.tennis },
  { id: "venue-catalunya", sport: "f1", name: "巴塞罗那-加泰罗尼亚赛道", city: "巴塞罗那", role: "F1第7轮", note: "传统赛道会检验车队综合速度和轮胎管理。", image: images.f1 },
  { id: "venue-linyi", sport: "volleyball", name: "临沂赛区", city: "中国临沂", role: "VNL男排揭幕", note: "中国时区友好，适合中文观众直接跟进。", image: images.volleyball }
];

const watchTasks = [
  { id: "task-nba", sport: "basketball", time: "06-11 08:30", title: "看NBA总决赛G4首节", detail: "重点观察尼克斯是否主动提速，以及马刺是否继续保护禁区。", priority: "高" },
  { id: "task-worldcup", sport: "football", time: "06-12 03:00", title: "世界杯揭幕战", detail: "开幕式、主队情绪和南非反击质量是三条主线。", priority: "高" },
  { id: "task-f1", sport: "f1", time: "06-14 21:00", title: "F1巴塞罗那正赛", detail: "记录轮胎衰退、进站窗口和升级包表现。", priority: "中" },
  { id: "task-wtt", sport: "tabletennis", time: "06-26", title: "US Smash资格赛首日", detail: "关注种子边缘球员和前三板质量。", priority: "中" },
  { id: "task-vnl", sport: "volleyball", time: "06-10 13:00", title: "男排VNL临沂揭幕", detail: "重点看波兰一传稳定性和古巴发球冲击。", priority: "中" },
  { id: "task-track", sport: "athletics", time: "本周", title: "追踪钻石联赛后续分站", detail: "记录短跑、110米栏、400米栏的赛季最佳变化。", priority: "低" }
];

const appOptions = {
  name: "App",
  data() {
    return {
    todayLabel: "2026年6月9日",
    sports,
    news,
    events,
    scoreboard,
    teamProfiles,
    players,
    trends,
    videoCards,
    leagueHubs,
    editorProfiles,
    newsletterPlans,
    smartLenses,
    featurePacks,
    standings,
    sportIndex,
    guideCards,
    tactics,
    venues,
    watchTasks,
    selectedSport: "all",
    query: "",
    minHeat: 0,
    activeView: "all",
    selectedSlot: "all",
    activeEvent: events[0],
    selectedStory: news[0],
    drawerOpen: false,
    savedIds: [],
    checkedTasks: [],
    activeTactic: tactics[0],
    compareA: "wemby",
    compareB: "antonelli",
    activeLens: "",
    lastAction: "",
    noticeTimer: null,
    scrollProgress: 0,
    showBackTop: false,
    newsletterEmail: "",
    subscribedPlan: "",
    timeSlots: [
      { id: "all", name: "全部" },
      { id: "morning", name: "上午" },
      { id: "midday", name: "中午" },
      { id: "evening", name: "晚间" },
      { id: "night", name: "凌晨" }
    ]
    };
  },

  computed: {
    routeName() {
      return this.$route.name || "home";
    },

    isHomeRoute() {
      return this.routeName === "home";
    },

    isDetailRoute() {
      return ["news-detail", "topic-detail", "guide-detail", "league-detail", "event-detail", "venue-detail", "match-detail", "player-detail", "video-detail", "team-detail", "author-detail"].indexOf(this.routeName) !== -1;
    },

    routePageConfig() {
      const pages = {
        scores: {
          eyebrow: "Score Center",
          title: "比分与赛况中心",
          intro: "像真实体育站一样把即时比分、系列赛态势和赛况详情拆成独立栏目。",
          sections: ["scores"],
          links: [{ label: "赛事日历", to: "/events" }, { label: "排名态势", to: "/rankings" }]
        },
        features: {
          eyebrow: "Special Desk",
          title: "赛事专题大厅",
          intro: "按赛事主线聚合新闻、战术、城市和观赛入口，适合连续追踪。",
          sections: ["features", "tactics", "index"],
          links: [{ label: "最新新闻", to: "/news" }, { label: "战术拆解", to: "/features" }]
        },
        guides: {
          eyebrow: "Guide Book",
          title: "观赛指南",
          intro: "给不同项目建立固定阅读路径，新用户也能快速进入比赛语境。",
          sections: ["guides", "index"],
          links: [{ label: "项目索引", to: "/features" }, { label: "数据实验室", to: "/data" }]
        },
        news: {
          eyebrow: "Newsroom",
          title: "最新新闻",
          intro: "新闻列表、专题摘要、收藏夹和视觉故事墙组成完整资讯频道。",
          sections: ["news", "gallery"],
          links: [{ label: "专题大厅", to: "/features" }, { label: "来源索引", to: "/sources" }]
        },
        events: {
          eyebrow: "Event Library",
          title: "赛事与日历",
          intro: "用时段筛选、赛事详情和观赛清单组织接下来要看的比赛。",
          sections: ["live", "events", "cities", "watch"],
          links: [{ label: "比分中心", to: "/scores" }, { label: "城市故事", to: "/events" }]
        },
        calendar: {
          eyebrow: "Calendar",
          title: "赛程日历",
          intro: "按日期组织观赛安排，适合真实网站里的可分享赛程页。",
          sections: ["calendar", "watch"],
          links: [{ label: "直播中心", to: "/live" }, { label: "观赛清单", to: "/watch" }]
        },
        leagues: {
          eyebrow: "League Hubs",
          title: "联赛中心",
          intro: "把NBA、世界杯、WTT、F1、VNL等赛事做成固定频道，形成真实门户的信息架构。",
          sections: ["leagues", "scores", "news", "videos"],
          links: [{ label: "新闻频道", to: "/news" }, { label: "赛程日历", to: "/calendar" }]
        },
        live: {
          eyebrow: "Live Desk",
          title: "直播中心",
          intro: "把即将开打、中文时区友好和高热赛事集中成直播工作台。",
          sections: ["live", "events", "watch", "videos"],
          links: [{ label: "观赛清单", to: "/watch" }, { label: "视频频道", to: "/videos" }]
        },
        videos: {
          eyebrow: "Video Hub",
          title: "视频频道",
          intro: "用短视频入口承载赛前板、技术拆解、专题影像和直播前瞻。",
          sections: ["videos", "news", "features"],
          links: [{ label: "直播中心", to: "/live" }, { label: "新闻频道", to: "/news" }]
        },
        teams: {
          eyebrow: "Team Index",
          title: "球队与阵容资料库",
          intro: "把比分、赛事和视频里出现的球队、国家队、赛事阵容做成可跳转资料页。",
          sections: ["teams", "scores", "players"],
          links: [{ label: "比分中心", to: "/scores" }, { label: "运动员聚焦", to: "/players" }]
        },
        rankings: {
          eyebrow: "Rank Room",
          title: "积分与排名态势",
          intro: "按项目整理系列赛、赛季指标、冠军走势和赛程压力。",
          sections: ["rankings", "data"],
          links: [{ label: "数据实验室", to: "/data" }, { label: "运动员聚焦", to: "/players" }]
        },
        players: {
          eyebrow: "Athlete Focus",
          title: "运动员与队伍",
          intro: "聚焦关键人物、球队样本和跨项目对比，形成更真实的球员资料入口。",
          sections: ["players", "data"],
          links: [{ label: "数据实验室", to: "/data" }, { label: "新闻频道", to: "/news" }]
        },
        data: {
          eyebrow: "Data Lab",
          title: "数据实验室",
          intro: "把热度、赛程密度、球员状态和跨项目对比集中呈现。",
          sections: ["data", "rankings"],
          links: [{ label: "排名态势", to: "/rankings" }, { label: "比分中心", to: "/scores" }]
        },
        watch: {
          eyebrow: "Watch List",
          title: "观赛清单",
          intro: "把接下来值得看的比赛做成可勾选的个人清单，适合真实站点里的我的赛程。",
          sections: ["watch", "events"],
          links: [{ label: "赛事日历", to: "/events" }, { label: "比分中心", to: "/scores" }]
        },
        cities: {
          eyebrow: "Host Cities",
          title: "城市与场馆",
          intro: "从场馆、城市和时区切入赛事，补足真实体育站常见的地域化内容。",
          sections: ["cities", "events", "features"],
          links: [{ label: "赛事日历", to: "/events" }, { label: "项目频道", to: "/features" }]
        },
        newsletter: {
          eyebrow: "Newsletter",
          title: "订阅中心",
          intro: "真实网站会用订阅入口承接回访，这里做成静态交互，用本地提示模拟订阅成功。",
          sections: ["newsletter"],
          links: [{ label: "新闻频道", to: "/news" }, { label: "赛程日历", to: "/calendar" }]
        },
        sources: {
          eyebrow: "Sources",
          title: "数据与图片来源",
          intro: "所有外部报道、赛程页面和图片来源集中在这里，方便回到原站核对。",
          sections: ["sources"],
          links: [{ label: "新闻频道", to: "/news" }, { label: "赛事日历", to: "/events" }]
        }
      };

      if (this.routeName === "tag") {
        const tag = this.$route.params.tag || "全部";
        return {
          eyebrow: "Topic Tag",
          title: `标签：${tag}`,
          intro: "标签页会聚合相关文章、赛事和图片入口，是门户站常用的横向浏览方式。",
          sections: ["news", "gallery"],
          links: [{ label: "新闻频道", to: "/news" }, { label: "专题大厅", to: "/features" }]
        };
      }

      if (this.routeName === "search") {
        return {
          eyebrow: "Search",
          title: this.$route.params.keyword ? `搜索：${this.$route.params.keyword}` : "全站搜索",
          intro: "通过路由承载搜索结果，用户可以把搜索页直接分享给别人。",
          sections: ["news", "events", "players", "gallery"],
          links: [{ label: "新闻频道", to: "/news" }, { label: "赛事日历", to: "/events" }]
        };
      }

      if (this.routeName === "calendar-date") {
        return {
          eyebrow: "Calendar",
          title: `赛程：${this.$route.params.date}`,
          intro: "日期页会筛出某一天的比赛，并保留直播、球队和项目频道跳转。",
          sections: ["calendar"],
          links: [{ label: "全部日期", to: "/calendar" }, { label: "直播中心", to: "/live" }]
        };
      }

      if (this.routeName === "sport") {
        return {
          eyebrow: "Sport Channel",
          title: `${this.activeSport.name}频道`,
          intro: this.activeSport.intro,
          sections: ["scores", "live", "features", "guides", "news", "tactics", "events", "cities", "rankings", "watch", "players", "teams", "index", "data", "videos", "gallery"],
          links: [{ label: "新闻", to: "/news" }, { label: "赛事", to: "/events" }, { label: "数据", to: "/data" }]
        };
      }

      if (this.routeDetail) {
        return {
          eyebrow: this.routeDetail.eyebrow,
          title: this.routeDetail.title,
          intro: this.routeDetail.body,
          sections: [],
          links: this.routeDetail.links
        };
      }

      return pages[this.routeName] || pages.news;
    },

    routeTitle() {
      return this.routePageConfig.title;
    },

    routeEyebrow() {
      return this.routePageConfig.eyebrow;
    },

    routeIntro() {
      return this.routePageConfig.intro;
    },

    routeQuickLinks() {
      return this.routePageConfig.links || [];
    },

    headlineLinks() {
      const storyLinks = this.news.slice(0, 4).map((item) => ({
        label: `${this.sportById(item.sport).name}｜${item.title}`,
        to: `/news/${item.id}`
      }));
      return storyLinks.concat([
        { label: "直播中心正在更新", to: "/live" },
        { label: "联赛中心已上线", to: "/leagues" }
      ]);
    },

    routeHeroStyle() {
      return { "--hero-image": `url("${this.activeSport.image}")` };
    },

    routeDetail() {
      if (this.routeName === "news-detail") {
        const item = this.news.find((story) => story.id === this.$route.params.id);
        if (!item) return null;
        const sport = this.sportById(item.sport);
        return {
          color: sport.color,
          image: item.image,
          eyebrow: `${sport.name} / ${item.date}`,
          title: item.title,
          body: item.detail,
          meta: [
            { label: "类型", value: this.viewLabel(item.type) },
            { label: "热度", value: item.heat },
            { label: "来源", value: item.sourceName }
          ],
          points: item.points,
          sourceUrl: item.sourceUrl,
          links: [{ label: "返回新闻", to: "/news" }, { label: `${sport.name}频道`, to: `/sports/${item.sport}` }]
        };
      }

      if (this.routeName === "topic-detail") {
        const pack = this.featurePacks.find((item) => item.id === this.$route.params.id);
        if (!pack) return null;
        const sport = this.sportById(pack.sport);
        return {
          color: sport.color,
          image: sport.image,
          eyebrow: `${sport.name} / Special Desk`,
          title: pack.title,
          body: pack.summary,
          meta: pack.metrics,
          points: ["专题页聚合赛事背景、关键指标和后续入口。", "同项目新闻、赛程、排名会在频道页继续展开。", "适合作为真实门户里的专题落地页。"],
          sourceUrl: "",
          links: [{ label: "返回专题", to: "/features" }, { label: `${sport.name}频道`, to: `/sports/${pack.sport}` }, { label: "相关新闻", to: "/news" }]
        };
      }

      if (this.routeName === "league-detail") {
        const league = this.leagueHubs.find((item) => item.id === this.$route.params.id);
        if (!league) return null;
        const sport = this.sportById(league.sport);
        return {
          color: sport.color,
          image: league.image,
          eyebrow: `${league.organizer} / ${league.season}`,
          title: league.name,
          body: league.summary,
          meta: [
            { label: "窗口", value: league.window },
            { label: "状态", value: league.status },
            { label: "项目", value: sport.name }
          ],
          points: league.tags.map((tag) => `重点标签：${tag}`),
          sourceUrl: "",
          links: [{ label: "返回联赛", to: "/leagues" }, { label: `${sport.name}频道`, to: `/sports/${league.sport}` }, { label: "赛程日历", to: "/calendar" }]
        };
      }

      if (this.routeName === "guide-detail") {
        const guide = this.guideCards.find((item) => item.id === this.$route.params.id);
        if (!guide) return null;
        const sport = this.sportById(guide.sport);
        return {
          color: sport.color,
          image: sport.image,
          eyebrow: `${sport.name} / Guide Book`,
          title: guide.title,
          body: guide.summary,
          meta: [
            { label: "难度", value: guide.level },
            { label: "项目", value: sport.name },
            { label: "来源", value: guide.sourceName }
          ],
          points: guide.items,
          sourceUrl: guide.sourceUrl,
          links: [{ label: "返回指南", to: "/guides" }, { label: `${sport.name}频道`, to: `/sports/${guide.sport}` }]
        };
      }

      if (this.routeName === "event-detail") {
        const event = this.events.find((item) => item.id === this.$route.params.id);
        if (!event) return null;
        const sport = this.sportById(event.sport);
        return {
          color: sport.color,
          image: sport.image,
          eyebrow: `${sport.name} / ${event.date}`,
          title: event.title,
          body: event.note,
          meta: [
            { label: "时间", value: event.time },
            { label: "地点", value: event.place },
            { label: "时段", value: this.slotLabel(event.slot) }
          ],
          points: [event.watch, "可在赛事频道切换时段查看同类赛程。", "项目频道会同步呈现相关新闻、排名和运动员焦点。"],
          sourceUrl: event.sourceUrl,
          links: [{ label: "返回赛事", to: "/events" }, { label: `${sport.name}频道`, to: `/sports/${event.sport}` }]
        };
      }

      if (this.routeName === "venue-detail") {
        const venue = this.venues.find((item) => item.id === this.$route.params.id);
        if (!venue) return null;
        const sport = this.sportById(venue.sport);
        return {
          color: sport.color,
          image: venue.image,
          eyebrow: `${venue.city} / ${sport.name}`,
          title: venue.name,
          body: venue.note,
          meta: [
            { label: "城市", value: venue.city },
            { label: "角色", value: venue.role },
            { label: "项目", value: sport.name }
          ],
          points: ["场馆页把赛事放进城市语境里。", "项目频道会继续呈现相关新闻、赛程和运动员。", "适合做真实网站里的城市/场馆垂直入口。"],
          sourceUrl: "",
          links: [{ label: "返回城市", to: "/cities" }, { label: `${sport.name}频道`, to: `/sports/${venue.sport}` }, { label: "赛事日历", to: "/events" }]
        };
      }

      if (this.routeName === "match-detail") {
        const match = this.scoreboard.find((item) => item.id === this.$route.params.id);
        if (!match) return null;
        const sport = this.sportById(match.sport);
        const related = this.news.find((item) => item.sport === match.sport);
        return {
          color: sport.color,
          image: sport.image,
          eyebrow: `${sport.name} / ${match.status}`,
          title: `${match.home} vs ${match.away}`,
          body: match.note,
          meta: [
            { label: "主队", value: `${match.home} ${match.homeScore}` },
            { label: "客队", value: `${match.away} ${match.awayScore}` },
            { label: "状态", value: match.status }
          ],
          points: related ? related.points : ["赛况会同步关联新闻、赛程和项目频道。"],
          sourceUrl: related ? related.sourceUrl : "",
          links: [{ label: "返回比分", to: "/scores" }, { label: `${sport.name}频道`, to: `/sports/${match.sport}` }]
        };
      }

      if (this.routeName === "player-detail") {
        const player = this.playerById(this.$route.params.id);
        if (!player) return null;
        const sport = this.sportById(player.sport);
        return {
          color: sport.color,
          image: player.image,
          eyebrow: `${sport.name} / Athlete Focus`,
          title: player.name,
          body: player.role,
          meta: player.stats.map((stat) => ({ label: stat.label, value: stat.value })),
          points: ["可加入数据实验室做跨项目对比。", "相关项目频道会同步呈现新闻、赛程、专题和排名。", "适合做真实网站里的运动员资料页雏形。"],
          sourceUrl: "",
          links: [{ label: "返回球星", to: "/players" }, { label: `${sport.name}频道`, to: `/sports/${player.sport}` }, { label: "数据实验室", to: "/data" }]
        };
      }

      if (this.routeName === "video-detail") {
        const video = this.videoCards.find((item) => item.id === this.$route.params.id);
        if (!video) return null;
        const sport = this.sportById(video.sport);
        return {
          color: sport.color,
          image: video.image,
          eyebrow: `${sport.name} / ${video.label}`,
          title: video.title,
          body: video.summary,
          meta: [
            { label: "时长", value: video.duration },
            { label: "类型", value: video.label },
            { label: "项目", value: sport.name }
          ],
          points: ["视频详情页承载赛前板、技术拆解和专题影像。", "可从这里继续跳转到赛事、项目频道和直播中心。", "这是静态门户模拟真实视频频道的落地页。"],
          sourceUrl: "",
          links: [{ label: "返回视频", to: "/videos" }, { label: `${sport.name}频道`, to: `/sports/${video.sport}` }, { label: "直播中心", to: "/live" }]
        };
      }

      if (this.routeName === "team-detail") {
        const team = this.teamById(this.$route.params.id);
        if (!team) return null;
        const sport = this.sportById(team.sport);
        return {
          color: sport.color,
          image: team.image,
          eyebrow: `${sport.name} / ${team.type}`,
          title: team.name,
          body: team.summary,
          meta: [
            { label: "城市", value: team.city },
            { label: "状态", value: team.form },
            { label: "场馆", value: team.venue }
          ],
          points: team.tags.map((tag) => `关联标签：${tag}`),
          sourceUrl: "",
          links: [{ label: "返回球队库", to: "/teams" }, { label: `${sport.name}频道`, to: `/sports/${team.sport}` }, { label: "相关赛程", to: "/events" }]
        };
      }

      if (this.routeName === "author-detail") {
        const editor = this.editorById(this.$route.params.id);
        if (!editor) return null;
        return {
          color: "#2a9d8f",
          image: editor.image,
          eyebrow: `${editor.role} / ${editor.beat}`,
          title: editor.name,
          body: editor.summary,
          meta: [
            { label: "职责", value: editor.role },
            { label: "方向", value: editor.beat },
            { label: "更新", value: this.todayLabel }
          ],
          points: ["作者页用于承载编辑组介绍、报道方向和相关内容入口。", "真实网站通常会把文章署名、栏目运营和编辑资料互相串联。", "本站用静态数据模拟作者中心。"],
          sourceUrl: "",
          links: [{ label: "新闻频道", to: "/news" }, { label: "直播中心", to: "/live" }, { label: "订阅中心", to: "/newsletter" }]
        };
      }

      return null;
    },

    routeRelatedLinks() {
      if (!this.routeDetail) return [];
      const sportId = this.selectedSport === "all" ? "" : this.selectedSport;
      const color = this.activeSport.color;
      const links = [];

      this.news
        .filter((item) => (!sportId || item.sport === sportId) && this.$route.fullPath !== `/news/${item.id}`)
        .slice(0, 2)
        .forEach((item) => {
          links.push({
            type: "新闻",
            title: item.title,
            copy: item.summary,
            to: `/news/${item.id}`,
            color: this.sportById(item.sport).color
          });
        });

      this.events
        .filter((item) => !sportId || item.sport === sportId)
        .slice(0, 1)
        .forEach((item) => {
          links.push({
            type: "赛事",
            title: item.title,
            copy: `${item.time} · ${item.place}`,
            to: `/events/${item.id}`,
            color: this.sportById(item.sport).color
          });
        });

      const player = this.players.find((item) => !sportId || item.sport === sportId);
      if (player) {
        links.push({
          type: "人物",
          title: player.name,
          copy: player.role,
          to: `/players/${player.id}`,
          color
        });
      }

      const team = this.teamProfiles.find((item) => !sportId || item.sport === sportId);
      if (team) {
        links.push({
          type: "队伍",
          title: team.name,
          copy: `${team.city} · ${team.form}`,
          to: `/teams/${team.id}`,
          color
        });
      }

      const video = this.videoCards.find((item) => !sportId || item.sport === sportId);
      if (video) {
        links.push({
          type: "视频",
          title: video.title,
          copy: `${video.label} · ${video.duration}`,
          to: `/videos/${video.id}`,
          color
        });
      }

      const topic = this.featurePacks.find((item) => !sportId || item.sport === sportId);
      if (topic) {
        links.push({
          type: "专题",
          title: topic.title,
          copy: topic.summary,
          to: `/topics/${topic.id}`,
          color
        });
      }

      return links.slice(0, 4);
    },

    activeSport() {
      return this.sportById(this.selectedSport);
    },

    activeSportTitle() {
      return this.selectedSport === "all" ? "全场" : this.activeSport.name;
    },

    hasFilters() {
      return (
        this.selectedSport !== "all" ||
        this.query !== "" ||
        this.minHeat > 0 ||
        this.activeView !== "all" ||
        this.selectedSlot !== "all"
      );
    },

    activeFilters() {
      const filters = [];
      if (this.selectedSport !== "all") {
        filters.push({ id: "sport", label: `项目：${this.activeSport.name}` });
      }
      if (this.query) {
        filters.push({ id: "query", label: `搜索：${this.query}` });
      }
      if (this.minHeat > 0) {
        filters.push({ id: "heat", label: `热度≥${this.minHeat}` });
      }
      if (this.activeView !== "all") {
        filters.push({ id: "view", label: `内容：${this.viewLabel(this.activeView)}` });
      }
      if (this.selectedSlot !== "all") {
        filters.push({ id: "slot", label: `时段：${this.slotLabel(this.selectedSlot)}` });
      }
      return filters;
    },

    filterSummary() {
      if (!this.hasFilters) return "全场内容已展开";
      return `${this.activeSportTitle} · ${this.viewLabel(this.activeView)} · ${this.slotLabel(this.selectedSlot)}`;
    },

    resultSummary() {
      return `${this.filteredNews.length}条新闻 / ${this.visibleEvents.length}场赛程 / ${this.visibleTeams.length}个队伍 / ${this.visibleVideos.length}条视频`;
    },

    heroStyle() {
      return { "--hero-image": `url("${this.activeSport.image}")` };
    },

    filteredNews() {
      const keyword = this.query.toLowerCase();
      return this.news.filter((item) => {
        const sportMatch = this.selectedSport === "all" || item.sport === this.selectedSport;
        const viewMatch = this.activeView === "all" || item.type === this.activeView;
        const heatMatch = item.heat >= this.minHeat;
        const haystack = [item.title, item.summary, item.detail, item.tags.join(" "), this.sportById(item.sport).name]
          .join(" ")
          .toLowerCase();
        return sportMatch && viewMatch && heatMatch && haystack.indexOf(keyword) !== -1;
      });
    },

    visibleEvents() {
      const keyword = this.query.toLowerCase();
      return this.events.filter((event) => {
        const sportMatch = this.selectedSport === "all" || event.sport === this.selectedSport;
        const slotMatch = this.selectedSlot === "all" || event.slot === this.selectedSlot;
        const haystack = [event.title, event.note, event.place, event.watch, this.sportById(event.sport).name]
          .join(" ")
          .toLowerCase();
        return sportMatch && slotMatch && haystack.indexOf(keyword) !== -1;
      });
    },

    visibleScoreboard() {
      return this.scoreboard.filter((match) => this.selectedSport === "all" || match.sport === this.selectedSport);
    },

    visibleLeagueHubs() {
      const keyword = this.query.toLowerCase();
      return this.leagueHubs.filter((league) => {
        const sportMatch = this.selectedSport === "all" || league.sport === this.selectedSport;
        const haystack = [league.name, league.organizer, league.season, league.status, league.summary, league.tags.join(" ")]
          .join(" ")
          .toLowerCase();
        return sportMatch && haystack.indexOf(keyword) !== -1;
      });
    },

    calendarDates() {
      return Array.from(new Set(this.events.map((event) => event.date)));
    },

    calendarEvents() {
      if (this.routeName === "calendar-date") {
        return this.visibleEvents.filter((event) => event.date === this.$route.params.date);
      }
      return this.visibleEvents;
    },

    visibleTeams() {
      const keyword = this.query.toLowerCase();
      return this.teamProfiles.filter((team) => {
        const sportMatch = this.selectedSport === "all" || team.sport === this.selectedSport;
        const haystack = [team.name, team.city, team.type, team.form, team.venue, team.summary, team.tags.join(" ")]
          .join(" ")
          .toLowerCase();
        return sportMatch && haystack.indexOf(keyword) !== -1;
      });
    },

    visibleVideos() {
      const keyword = this.query.toLowerCase();
      return this.videoCards.filter((video) => {
        const sportMatch = this.selectedSport === "all" || video.sport === this.selectedSport;
        const haystack = [video.title, video.label, video.summary, this.sportById(video.sport).name]
          .join(" ")
          .toLowerCase();
        return sportMatch && haystack.indexOf(keyword) !== -1;
      });
    },

    liveCards() {
      return this.visibleEvents.slice(0, 5).map((event) => ({
        ...event,
        sportName: this.sportById(event.sport).name,
        color: this.sportById(event.sport).color,
        teamLinks: this.teamsFromText(event.title)
      }));
    },

    visibleFeaturePacks() {
      return this.featurePacks.filter((pack) => this.selectedSport === "all" || pack.sport === this.selectedSport);
    },

    visibleStandings() {
      return this.standings.filter((table) => this.selectedSport === "all" || table.sport === this.selectedSport);
    },

    visibleGuides() {
      return this.guideCards.filter((guide) => this.selectedSport === "all" || guide.sport === this.selectedSport);
    },

    visibleTactics() {
      return this.tactics.filter((tactic) => this.selectedSport === "all" || tactic.sport === this.selectedSport);
    },

    visibleVenues() {
      return this.venues.filter((venue) => this.selectedSport === "all" || venue.sport === this.selectedSport);
    },

    visibleWatchTasks() {
      return this.watchTasks.filter((task) => this.selectedSport === "all" || task.sport === this.selectedSport);
    },

    taskProgress() {
      if (!this.visibleWatchTasks.length) return 0;
      const done = this.visibleWatchTasks.filter((task) => this.checkedTasks.indexOf(task.id) !== -1).length;
      return Math.round((done / this.visibleWatchTasks.length) * 100);
    },

    filteredPlayers() {
      const keyword = this.query.toLowerCase();
      return this.players.filter((player) => {
        const sportMatch = this.selectedSport === "all" || player.sport === this.selectedSport;
        const haystack = [player.name, player.role, this.sportById(player.sport).name].join(" ").toLowerCase();
        return sportMatch && haystack.indexOf(keyword) !== -1;
      });
    },

    pulseScore() {
      if (!this.filteredNews.length) return 0;
      const total = this.filteredNews.reduce((sum, item) => sum + item.heat, 0);
      return Math.round(total / this.filteredNews.length);
    },

    savedStories() {
      return this.news.filter((item) => this.savedIds.indexOf(item.id) !== -1);
    },

    savedCount() {
      return this.savedIds.length;
    },

    sourceList() {
      const sourceMap = {};
      this.news.forEach((item) => {
        sourceMap[item.sourceUrl] = { name: item.sourceName, url: item.sourceUrl };
      });
      this.events.forEach((item) => {
        sourceMap[item.sourceUrl] = { name: this.sportById(item.sport).name + "赛程", url: item.sourceUrl };
      });
      this.guideCards.forEach((item) => {
        sourceMap[item.sourceUrl] = { name: item.sourceName, url: item.sourceUrl };
      });
      sourceMap["https://images.unsplash.com/"] = { name: "Unsplash 图片 CDN", url: "https://images.unsplash.com/" };
      return Object.keys(sourceMap).map((key) => sourceMap[key]);
    },

    tickerItems() {
      const base = this.trends.map((trend) => `${this.sportById(trend.sport).name} · ${trend.label}`);
      return base.concat(base);
    },

    sportMetrics() {
      return this.sports
        .filter((sport) => sport.id !== "all")
        .map((sport) => {
          const stories = this.news.filter((item) => item.sport === sport.id);
          const heat = stories.length
            ? Math.round(stories.reduce((sum, item) => sum + item.heat, 0) / stories.length)
            : 0;
          return { id: sport.id, name: sport.name, color: sport.color, heat, count: stories.length };
        })
        .sort((a, b) => b.heat - a.heat);
    },

    comparisonPlayers() {
      return [this.playerById(this.compareA), this.playerById(this.compareB)].filter(Boolean);
    },

    galleryStories() {
      return this.filteredNews.slice(0, 8);
    }
  },

  watch: {
    selectedSport() {
      this.ensureVisibleSelections();
      this.renderIcons();
    },
    selectedSlot() {
      this.ensureVisibleSelections();
    },
    activeView() {
      this.ensureVisibleSelections();
    },
    minHeat() {
      this.ensureVisibleSelections();
    },
    query() {
      this.ensureVisibleSelections();
    },
    savedIds: {
      handler(ids) {
        localStorage.setItem("sportsHubSaved", JSON.stringify(ids));
      },
      deep: true
    },
    checkedTasks: {
      handler(ids) {
        localStorage.setItem("sportsHubTasks", JSON.stringify(ids));
      },
      deep: true
    },
    $route: {
      immediate: true,
      handler() {
        this.syncRouteState();
      }
    }
  },

  mounted() {
    try {
      this.savedIds = JSON.parse(localStorage.getItem("sportsHubSaved") || "[]");
      this.checkedTasks = JSON.parse(localStorage.getItem("sportsHubTasks") || "[]");
    } catch (error) {
      this.savedIds = [];
      this.checkedTasks = [];
    }
    this.ensureVisibleSelections();
    this.renderIcons();
    this.handleScroll();
    window.addEventListener("scroll", this.handleScroll, { passive: true });
    window.addEventListener("keydown", this.handleKeydown);
  },

  updated() {
    this.renderIcons();
  },

  beforeDestroy() {
    if (this.noticeTimer) {
      clearTimeout(this.noticeTimer);
    }
    window.removeEventListener("scroll", this.handleScroll);
    window.removeEventListener("keydown", this.handleKeydown);
  },

  methods: {
    handleScroll() {
      const doc = document.documentElement;
      const max = Math.max(doc.scrollHeight - window.innerHeight, 1);
      const progress = Math.min(100, Math.max(0, Math.round((window.scrollY / max) * 100)));
      this.scrollProgress = progress;
      this.showBackTop = window.scrollY > 360;
    },

    handleKeydown(event) {
      if (event.key === "Escape" && this.drawerOpen) {
        this.drawerOpen = false;
      }
    },

    scrollToTop() {
      window.scrollTo({ top: 0, behavior: "smooth" });
      this.showNotice("已回到页面顶部");
    },

    sportById(id) {
      return this.sports.find((sport) => sport.id === id) || this.sports[0];
    },

    playerById(id) {
      return this.players.find((player) => player.id === id);
    },

    teamById(id) {
      return this.teamProfiles.find((team) => team.id === id);
    },

    editorById(id) {
      return this.editorProfiles.find((editor) => editor.id === id);
    },

    teamByName(name) {
      const compact = (name || "").toLowerCase().replace(/\s+/g, "");
      return this.teamProfiles.find((team) => {
        const teamName = team.name.toLowerCase().replace(/\s+/g, "");
        return compact === teamName || compact.indexOf(teamName) !== -1 || teamName.indexOf(compact) !== -1;
      });
    },

    teamsFromText(text) {
      return this.teamProfiles.filter((team) => (text || "").indexOf(team.name) !== -1).slice(0, 3);
    },

    pushRoute(location) {
      const resolved = this.$router.resolve(location).route;
      if (resolved.fullPath === this.$route.fullPath) return;
      this.$router.push(location).catch(() => {});
    },

    showSection(section) {
      if (this.isDetailRoute) return false;
      if (this.isHomeRoute) return true;
      return (this.routePageConfig.sections || []).indexOf(section) !== -1;
    },

    syncRouteState() {
      if (!this.$route) return;
      const name = this.$route.name || "home";
      const sectionRoutes = ["scores", "features", "guides", "news", "events", "calendar", "leagues", "rankings", "players", "data", "live", "videos", "teams", "watch", "cities", "newsletter", "sources"];
      let nextSport = "all";

      if (name === "sport") {
        nextSport = this.sportById(this.$route.params.id).id;
      }

      if (name === "news-detail") {
        const story = this.news.find((item) => item.id === this.$route.params.id);
        nextSport = story ? story.sport : "all";
        this.selectedStory = story || this.news[0];
      }

      if (name === "topic-detail") {
        const topic = this.featurePacks.find((item) => item.id === this.$route.params.id);
        nextSport = topic ? topic.sport : "all";
      }

      if (name === "league-detail") {
        const league = this.leagueHubs.find((item) => item.id === this.$route.params.id);
        nextSport = league ? league.sport : "all";
      }

      if (name === "guide-detail") {
        const guide = this.guideCards.find((item) => item.id === this.$route.params.id);
        nextSport = guide ? guide.sport : "all";
      }

      if (name === "event-detail") {
        const event = this.events.find((item) => item.id === this.$route.params.id);
        nextSport = event ? event.sport : "all";
        this.activeEvent = event || this.events[0];
      }

      if (name === "venue-detail") {
        const venue = this.venues.find((item) => item.id === this.$route.params.id);
        nextSport = venue ? venue.sport : "all";
      }

      if (name === "match-detail") {
        const match = this.scoreboard.find((item) => item.id === this.$route.params.id);
        nextSport = match ? match.sport : "all";
      }

      if (name === "player-detail") {
        const player = this.playerById(this.$route.params.id);
        nextSport = player ? player.sport : "all";
      }

      if (name === "video-detail") {
        const video = this.videoCards.find((item) => item.id === this.$route.params.id);
        nextSport = video ? video.sport : "all";
      }

      if (name === "team-detail") {
        const team = this.teamById(this.$route.params.id);
        nextSport = team ? team.sport : "all";
      }

      if (sectionRoutes.indexOf(name) !== -1 || name === "home") {
        this.query = "";
        this.minHeat = 0;
        this.activeView = "all";
        this.selectedSlot = "all";
      }

      if (name === "tag") {
        this.query = this.$route.params.tag || "";
        this.minHeat = 0;
        this.activeView = "all";
        this.selectedSlot = "all";
      }

      if (name === "search") {
        this.query = this.$route.params.keyword || "";
        this.minHeat = 0;
        this.activeView = "all";
        this.selectedSlot = "all";
      }

      this.selectedSport = nextSport;
      this.activeLens = "";
      this.ensureVisibleSelections();
      this.renderIcons();
    },

    goSportChannel(id) {
      if (id === "all") {
        this.resetAll();
        this.pushRoute("/");
        return;
      }
      this.pushRoute({ name: "sport", params: { id } });
    },

    goTopic(topic) {
      this.pushRoute({ name: "topic-detail", params: { id: topic.id } });
    },

    goLeague(league) {
      this.pushRoute({ name: "league-detail", params: { id: league.id } });
    },

    goGuide(guide) {
      this.pushRoute({ name: "guide-detail", params: { id: guide.id } });
    },

    goStory(item) {
      this.pushRoute({ name: "news-detail", params: { id: item.id } });
    },

    goEvent(event) {
      this.pushRoute({ name: "event-detail", params: { id: event.id } });
    },

    goMatch(match) {
      this.pushRoute({ name: "match-detail", params: { id: match.id } });
    },

    goPlayer(player) {
      this.pushRoute({ name: "player-detail", params: { id: player.id } });
    },

    goVideo(video) {
      this.pushRoute({ name: "video-detail", params: { id: video.id } });
    },

    goTeam(team) {
      this.pushRoute({ name: "team-detail", params: { id: team.id } });
    },

    goTeamByName(name) {
      const team = this.teamByName(name);
      if (team) {
        this.goTeam(team);
      }
    },

    goVenue(venue) {
      this.pushRoute({ name: "venue-detail", params: { id: venue.id } });
    },

    goTag(tag) {
      this.pushRoute({ name: "tag", params: { tag } });
    },

    goSearch(keyword) {
      const trimmed = (keyword || "").trim();
      if (trimmed) {
        this.pushRoute({ name: "search", params: { keyword: trimmed } });
      } else {
        this.pushRoute({ name: "search" });
      }
    },

    subscribePlan(plan) {
      this.subscribedPlan = plan.id;
      this.showNotice(this.newsletterEmail ? `已订阅：${plan.title}` : `已选择：${plan.title}`);
    },

    selectSport(id) {
      this.selectedSport = id;
      this.activeLens = "";
      this.showNotice(`已切换到${this.sportById(id).name}`);
    },

    resetAll() {
      this.selectedSport = "all";
      this.query = "";
      this.minHeat = 0;
      this.activeView = "all";
      this.selectedSlot = "all";
      this.activeEvent = this.events[0];
      this.selectedStory = this.news[0];
      this.activeLens = "";
      if (this.routeName === "sport") {
        this.pushRoute("/");
      }
      this.showNotice("已回到全场视图");
    },

    applyLens(lens) {
      this.selectedSport = lens.sport;
      this.activeView = lens.view;
      this.selectedSlot = lens.slot;
      this.minHeat = lens.heat;
      this.query = lens.query;
      this.activeLens = lens.id;
      this.showNotice(`已切换：${lens.name}`);
    },

    clearFilter(id) {
      if (id === "sport") this.selectedSport = "all";
      if (id === "query") this.query = "";
      if (id === "heat") this.minHeat = 0;
      if (id === "view") this.activeView = "all";
      if (id === "slot") this.selectedSlot = "all";
      this.activeLens = "";
      this.showNotice("已移除筛选");
    },

    openStory(item) {
      this.goStory(item);
    },

    openMatch(match) {
      this.goMatch(match);
    },

    toggleSaved(id) {
      const index = this.savedIds.indexOf(id);
      if (index === -1) {
        this.savedIds.push(id);
        this.showNotice("已加入收藏夹");
      } else {
        this.savedIds.splice(index, 1);
        this.showNotice("已取消收藏");
      }
    },

    isSaved(id) {
      return this.savedIds.indexOf(id) !== -1;
    },

    selectPlayer(player) {
      if (this.compareA === player.id) return;
      this.compareB = player.id;
      if (this.routeName !== "data" && this.routeName !== "players") {
        this.pushRoute("/data");
      }
      this.$nextTick(() => {
        const target = document.getElementById("data");
        if (target) {
          target.scrollIntoView({ behavior: "smooth" });
        }
      });
      this.showNotice(`已加入对比：${player.name}`);
    },

    ensureVisibleSelections() {
      if (!this.visibleEvents.some((event) => this.activeEvent && event.id === this.activeEvent.id)) {
        this.activeEvent = this.visibleEvents[0] || null;
      }
      if (!this.filteredNews.some((item) => this.selectedStory && item.id === this.selectedStory.id)) {
        this.selectedStory = this.filteredNews[0] || null;
      }
      if (!this.visibleTactics.some((item) => this.activeTactic && item.id === this.activeTactic.id)) {
        this.activeTactic = this.visibleTactics[0] || null;
      }
    },

    renderIcons() {
      this.$nextTick(() => {
        createIcons({ icons: lucideIcons });
      });
    },

    viewLabel(id) {
      const labels = {
        all: "全部内容",
        breaking: "快讯",
        feature: "专题",
        analysis: "分析"
      };
      return labels[id] || "全部内容";
    },

    slotLabel(id) {
      const found = this.timeSlots.find((slot) => slot.id === id);
      return found ? found.name : "全部";
    },

    showNotice(message) {
      this.lastAction = message;
      if (this.noticeTimer) {
        clearTimeout(this.noticeTimer);
      }
      this.noticeTimer = setTimeout(() => {
        this.lastAction = "";
      }, 1700);
    }
  }
};

export default appOptions;
</script>
